// Outfit ideas for all 12 color analysis sub-seasons.
// Every color named below comes from the matching sub-season palette in constants/index.tsx (colorSeasons).
// Slug -> palette mapping: soft-summer uses the SUMMER.COOL palette (route /color-palette/summer/cool),
// bright-winter uses the WINTER.COOL palette (route /color-palette/winter/cool). All other slugs map to
// their namesake sub-season keys.

export interface SeasonOutfit {
  name: string;
  occasion: "casual" | "work" | "date" | "evening" | "weekend";
  description: string;
  pieces: string[];
  stylingNote: string;
}

export interface SeasonOutfitPageData {
  displayName: string;
  season: string;
  subSeason: string;
  intro: string;
  outfits: SeasonOutfit[];
  capsule: string[];
  faqs: { question: string; answer: string }[];
}

export const seasonOutfitsData: Record<string, SeasonOutfitPageData> = {
  "light-spring": {
    displayName: "Light Spring",
    season: "spring",
    subSeason: "light",
    intro:
      "Light Spring outfits work when everything stays light, warm, and gently clear - think sunlight, not shadow. Your coloring is delicate and golden-toned, so the trick is low contrast: pair soft pastels like apricot, mint green, and buttercream with warm light neutrals such as cream and soft camel instead of black or charcoal. Keep fabrics airy and colors fresh, and your skin will look lit from within.",
    outfits: [
      {
        name: "Apricot Coffee Run",
        occasion: "casual",
        description:
          "An easy off-duty look that swaps default grey-and-black for warm, sunlit softness. Perfect for errands that turn into brunch.",
        pieces: [
          "Apricot ribbed cotton tank",
          "Cream high-rise straight-leg jeans",
          "Soft camel suede ballet flats",
          "Light aqua canvas tote",
          "Pale gold huggie hoops",
        ],
        stylingNote:
          "Cream denim instead of dark indigo keeps the value light, so the warm apricot glows against your golden undertone rather than fighting a heavy base.",
      },
      {
        name: "Mint Morning Market",
        occasion: "casual",
        description:
          "A breezy warm-weather outfit built on fresh green and warm beige. It reads polished without a single structured piece.",
        pieces: [
          "Mint green cotton poplin shirt, worn open",
          "Buttercream jersey tee",
          "Warm beige linen shorts",
          "Cream leather sneakers",
          "Sky blue silk scarf tied to the bag strap",
        ],
        stylingNote:
          "Mint and buttercream sit at the same light value, giving the low-contrast blend that flatters Light Spring's delicate coloring.",
      },
      {
        name: "Blush Recovery Day",
        occasion: "casual",
        description:
          "Athleisure that flatters instead of washing you out. Ideal for pilates, school pickup, or a long walk.",
        pieces: [
          "Blush pink ribbed leggings",
          "Matching blush pink longline sports bra",
          "Cream half-zip fleece",
          "Pale taupe running sneakers",
          "Light periwinkle baseball cap",
        ],
        stylingNote:
          "Tonal blush head-to-toe keeps chroma soft and warm, which reads intentional on Light Spring where grey athleisure reads tired.",
      },
      {
        name: "Buttercream Boardroom",
        occasion: "work",
        description:
          "A light-neutral power outfit that carries authority without the harshness of a black suit.",
        pieces: [
          "Buttercream silk blouse with a soft bow neck",
          "Warm beige wide-leg trousers",
          "Soft camel knit blazer",
          "Cream pointed-toe pumps",
          "Pale gold link bracelet",
        ],
        stylingNote:
          "Stacking warm light neutrals creates a column of low-contrast polish - the Light Spring answer to corporate black.",
      },
      {
        name: "Periwinkle Presentation",
        occasion: "work",
        description:
          "One-and-done dressing for a day full of meetings, with a cool-leaning accent your palette handles beautifully.",
        pieces: [
          "Light periwinkle ponte sheath dress",
          "Cream collarless cropped jacket",
          "Warm beige slingback heels",
          "Soft melon leather portfolio bag",
        ],
        stylingNote:
          "Light periwinkle is one of the few blue tones light and clear enough to stay in harmony with your warm, light coloring.",
      },
      {
        name: "Coral Golden Hour",
        occasion: "date",
        description:
          "A sunset-dinner look that flatters bare skin and candlelight equally. Romantic without trying too hard.",
        pieces: [
          "Light coral bias-cut satin slip dress",
          "Cream cropped cashmere cardigan",
          "Pale gold strappy heeled sandals",
          "Blush pink lip and a pale gold locket",
        ],
        stylingNote:
          "Light coral echoes the natural flush in Light Spring skin, so the dress and your complexion amplify each other.",
      },
      {
        name: "Daffodil After Dark",
        occasion: "evening",
        description:
          "Proof you do not need black for evening. A luminous yellow that photographs like sunshine at a cocktail party.",
        pieces: [
          "Daffodil chiffon one-shoulder midi dress",
          "Pale gold box clutch",
          "Cream ankle-strap heels",
          "Light mint drop earrings",
        ],
        stylingNote:
          "Clear light yellow is a Light Spring signature - high enough in warmth and light enough in value to make your skin radiate under evening lighting.",
      },
      {
        name: "Sky Blue Saturday",
        occasion: "weekend",
        description:
          "A relaxed farmers-market-to-lunch uniform in your freshest blue.",
        pieces: [
          "Sky blue oversized linen shirt",
          "Cream denim shorts",
          "Mint green low-top canvas sneakers",
          "Pale taupe woven crossbody bag",
          "Sunlight yellow claw clip",
        ],
        stylingNote:
          "Sky blue is soft and light like a spring morning sky, keeping contrast gentle while the yellow accent adds warmth back in.",
      },
      {
        name: "Soft Melon Stroll",
        occasion: "casual",
        description:
          "A one-piece throw-on for hot days that still looks pulled together.",
        pieces: [
          "Soft melon cotton T-shirt dress",
          "Warm beige espadrille slides",
          "Light aqua beaded necklace",
          "Cream bucket hat",
        ],
        stylingNote:
          "Melon's warm, low-intensity glow flatters golden undertones in strong sun where saturated brights would overwhelm you.",
      },
      {
        name: "Cream Puff Layers",
        occasion: "casual",
        description:
          "Cold-weather casual that keeps Light Spring luminous when everyone else defaults to black puffers.",
        pieces: [
          "Cream teddy-fleece coat",
          "Blush pink merino turtleneck",
          "Warm beige straight-leg corduroy trousers",
          "Pale taupe shearling-lined ankle boots",
          "Pale gold beanie with a knit cuff",
        ],
        stylingNote:
          "Winter light is grey and flat, so keeping every layer warm and light-valued preserves the fresh contrast your face needs.",
      },
      {
        name: "Light Aqua Drizzle Day",
        occasion: "casual",
        description:
          "A rainy-day outfit with actual color in it. Water-friendly layers that stay in palette.",
        pieces: [
          "Soft camel water-resistant trench coat",
          "Light aqua crewneck sweater",
          "Cream slim-fit jeans",
          "Warm beige Chelsea rain boots",
          "Sky blue compact umbrella",
        ],
        stylingNote:
          "Camel is Light Spring's best coat neutral - it does the practical job of khaki or black while flattering warm, light coloring.",
      },
      {
        name: "Pale Taupe Power Suit",
        occasion: "work",
        description:
          "Your interview and big-pitch outfit: a full light-neutral suit warmed up with peach.",
        pieces: [
          "Pale taupe single-breasted suit blazer",
          "Pale taupe tailored cigarette trousers",
          "Peach silk shell top",
          "Cream leather loafers with pale gold hardware",
          "Warm beige structured tote",
        ],
        stylingNote:
          "A taupe suit gives you formal authority at a light value, and the peach shell bounces warmth up into your face where a white shirt would look stark.",
      },
      {
        name: "Lilac Casual Friday",
        occasion: "work",
        description:
          "Softly professional for hybrid offices - knitwear polish with a pastel lift.",
        pieces: [
          "Lilac fine-gauge crewneck sweater",
          "Cream pleated midi skirt",
          "Soft camel heeled ankle boots",
          "Pale gold watch with a leather strap",
        ],
        stylingNote:
          "Lilac from your complementary palette adds cool variety while staying pastel-light, so it never breaks Light Spring's low-contrast rule.",
      },
      {
        name: "Blush Picnic Date",
        occasion: "date",
        description:
          "A daytime date look that moves well - soft pink and periwinkle in floaty fabrics.",
        pieces: [
          "Blush pink wrap-front blouse with flutter sleeves",
          "Light periwinkle flowy midi skirt",
          "Cream braided flat sandals",
          "Pale gold anklet",
          "Buttercream mini shoulder bag",
        ],
        stylingNote:
          "Blush-plus-periwinkle is a warm-cool pastel pairing at matched lightness - romantic contrast without any heaviness.",
      },
      {
        name: "Peach Garden Wedding Guest",
        occasion: "evening",
        description:
          "A wedding-guest outfit that flatters in every photo, from ceremony sun to reception dusk.",
        pieces: [
          "Peach floral-print organza midi dress",
          "Cream satin wrap for the evening",
          "Pale gold block-heel sandals",
          "Light mint chandelier earrings",
          "Soft melon silk clutch",
        ],
        stylingNote:
          "Peach is the single most flattering formal color for Light Spring - it mirrors your undertone, so you glow instead of the dress wearing you.",
      },
      {
        name: "Sunlight Getaway Travel Set",
        occasion: "weekend",
        description:
          "An airport-to-arrival outfit in knit co-ords: comfortable for the flight, photo-ready on landing.",
        pieces: [
          "Sunlight yellow knitted co-ord tank and cardigan set",
          "Cream wide-leg jersey trousers",
          "Pale taupe slip-on sneakers",
          "Soft camel weekender bag",
          "Light aqua eye mask peeking from the pocket",
        ],
        stylingNote:
          "Clear light yellow keeps you looking awake after a red-eye - it adds the warmth and light your face loses under airport fluorescents.",
      },
    ],
    capsule: [
      "Cream high-rise straight-leg jeans",
      "Buttercream silk blouse",
      "Soft camel knit blazer",
      "Apricot ribbed cotton tank",
      "Light periwinkle ponte sheath dress",
      "Warm beige wide-leg trousers",
      "Mint green cotton poplin shirt",
      "Soft camel water-resistant trench coat",
      "Pale taupe suede ballet flats",
      "Light coral bias-cut satin slip dress",
    ],
    faqs: [
      {
        question: "Can a Light Spring wear black?",
        answer:
          "Black is on the Light Spring avoid list because its heavy, cool darkness overwhelms your light, warm coloring and casts shadows on the face. If a dress code demands it, keep black below the waist or far from your face, and swap in soft camel, cream, or warm beige whenever possible. A pale taupe or camel suit reads just as formal without draining your glow.",
      },
      {
        question: "What colors should a Light Spring wear to a wedding?",
        answer:
          "Reach for peach, apricot, light coral, or daffodil in floaty fabrics like chiffon and organza - they photograph beautifully on warm, light coloring. Pair with cream or pale gold accessories rather than black heels or clutches. Avoid deep navy and burgundy; both are on your avoid list and will look severe next to your delicate features.",
      },
      {
        question: "What color jeans look best on a Light Spring?",
        answer:
          "Cream, warm beige, and light warm-washed denim are your best jean colors because they keep your outfit's overall value light. Very dark indigo or black jeans create a harsh lower half that pulls attention away from your face. If you want contrast, get it from a light coral or mint top rather than from dark denim.",
      },
      {
        question: "What should a Light Spring wear in winter?",
        answer:
          "Build cold-weather outfits from cream, soft camel, and pale taupe outerwear instead of black or charcoal coats, then add warmth with blush pink or light aqua knits near the face. Texture - teddy fleece, cashmere, shearling - gives winter depth without darkening your palette. A camel trench or cream teddy coat is the Light Spring winter workhorse.",
      },
    ],
  },
  "true-spring": {
    displayName: "True Spring",
    season: "spring",
    subSeason: "true",
    intro:
      "True Spring is the warmest, sunniest corner of the color wheel: golden undertones, medium-light value, and colors that look freshly saturated rather than dusty. Your outfits should be built on clear warm hues - coral, jade, amber, turquoise - grounded by golden neutrals like warm ivory, sand, and light camel. If a color looks like it belongs in a tropical garden at noon, it belongs on you; if it looks muddy or greyed, leave it on the rack.",
    outfits: [
      {
        name: "Coral State of Mind",
        occasion: "casual",
        description:
          "The everyday True Spring formula: one juicy warm color, one golden neutral, zero mud.",
        pieces: [
          "Coral relaxed cotton crewneck tee",
          "Light camel straight-leg chinos",
          "Warm ivory leather sneakers",
          "Turquoise beaded bracelet stack",
          "Sand canvas baseball cap",
        ],
        stylingNote:
          "Coral against camel repeats the golden warmth of your skin and hair, so the whole outfit reads like an extension of your natural coloring.",
      },
      {
        name: "Jade Greenhouse Casual",
        occasion: "casual",
        description:
          "A fresh green look for garden centers, galleries, or long lunches on a patio.",
        pieces: [
          "Jade cotton camp-collar shirt",
          "Warm ivory wide-leg jeans",
          "Light camel woven leather slides",
          "Golden pendant necklace",
        ],
        stylingNote:
          "Jade is a warm, clear green with yellow in its base - it brightens golden undertones where cool forest greens would dull them.",
      },
      {
        name: "Tangerine Trail Mix",
        occasion: "casual",
        description:
          "Warm-toned activewear for hikes and gym days - energizing color that matches your energy.",
        pieces: [
          "Bright tangerine racerback tank",
          "Sand high-waisted biker shorts",
          "Warm ivory trail sneakers",
          "Turquoise water bottle sling",
          "Light camel zip-through windbreaker",
        ],
        stylingNote:
          "High-energy tangerine works because True Spring handles clarity and warmth together - the same shade would overpower a muted season.",
      },
      {
        name: "Amber Hour Office",
        occasion: "work",
        description:
          "Warm authority for client-facing days: golden tones that read confident and approachable at once.",
        pieces: [
          "Amber silk button-down blouse",
          "Warm ivory tailored trousers",
          "Light camel single-breasted blazer",
          "Sand pointed-toe leather flats",
          "Golden signet ring",
        ],
        stylingNote:
          "An ivory-camel-amber column keeps everything in one warm temperature family, which is what makes a True Spring look expensive.",
      },
      {
        name: "Azure Strategy Session",
        occasion: "work",
        description:
          "Your best blue at work - vivid enough to be memorable in the meeting, warm-compatible enough to flatter.",
        pieces: [
          "Azure fine-knit polo sweater",
          "Sand pleated-front trousers",
          "Warm ivory leather loafers",
          "Light camel structured tote",
          "Golden slim-band watch",
        ],
        stylingNote:
          "Azure is a clear, lively blue that gives True Spring cool contrast without the deadening effect of navy or charcoal.",
      },
      {
        name: "Watermelon Sunset Date",
        occasion: "date",
        description:
          "A flirty dinner look in your palette's most romantic pink-red.",
        pieces: [
          "Warm watermelon linen-blend midi dress with a tie waist",
          "Golden strappy heeled sandals",
          "Warm ivory crochet shoulder bag",
          "Coral lip tint and golden drop earrings",
        ],
        stylingNote:
          "Warm watermelon has enough yellow in it to flatter golden skin, unlike blue-based pinks that make True Springs look flushed in the wrong way.",
      },
      {
        name: "Emerald Evening Standard",
        occasion: "evening",
        description:
          "A jewel-toned cocktail outfit that holds its own in dim light without going dark.",
        pieces: [
          "Emerald satin wrap midi dress",
          "Golden metallic ankle-strap heels",
          "Warm ivory faux-fur stole",
          "Golden cuff bracelet",
          "Turquoise cocktail ring",
        ],
        stylingNote:
          "Emerald is the deepest color True Spring should go, and its clarity keeps it luminous on you where black would flatten your warmth.",
      },
      {
        name: "Turquoise Roadtrip",
        occasion: "weekend",
        description:
          "A wear-everywhere weekend look for beach towns, road trips, and boardwalk ice cream.",
        pieces: [
          "Turquoise gauze cotton shirt, half buttoned",
          "Warm ivory denim cutoff shorts",
          "Sand leather flip-flop sandals",
          "Light camel straw tote",
          "Golden shell anklet",
        ],
        stylingNote:
          "Turquoise reads as sun-drenched ocean rather than cool water - exactly the warm-leaning clarity your coloring asks for.",
      },
      {
        name: "Golden Layer Cake",
        occasion: "casual",
        description:
          "Transitional-weather layering with a golden thread running through it.",
        pieces: [
          "Golden lightweight merino turtleneck",
          "Light camel suede shirt jacket",
          "Warm ivory straight jeans",
          "Sand leather ankle boots",
          "Amber resin hoop earrings",
        ],
        stylingNote:
          "Golden yellow near the face acts like a reflector, bouncing warmth upward - it is the single most identifying True Spring color.",
      },
      {
        name: "Poppy Puffer Season",
        occasion: "casual",
        description:
          "Winter casual with a heartbeat: a warm red coat over golden neutrals.",
        pieces: [
          "Warm poppy quilted puffer jacket",
          "Warm ivory chunky cable-knit sweater",
          "Light camel corduroy straight trousers",
          "Sand lace-up hiking boots",
          "Golden knit scarf",
        ],
        stylingNote:
          "Warm poppy is a yellow-based red that keeps its fire on golden skin - the winter antidote to a sea of black outerwear.",
      },
      {
        name: "Orange Crush Errands",
        occasion: "casual",
        description:
          "A simple sweater-and-denim combination that proves orange is a neutral for True Springs.",
        pieces: [
          "Orange cotton-knit crewneck sweater",
          "Sand relaxed barrel-leg jeans",
          "Warm ivory retro sneakers",
          "Turquoise enamel pendant",
        ],
        stylingNote:
          "True Spring is the season that wears true orange best - it matches your undertone so closely it behaves like a neutral on you.",
      },
      {
        name: "Sapphire Boardroom Counterpoint",
        occasion: "work",
        description:
          "For formal days: your palette's most serious color, worn warm.",
        pieces: [
          "Sapphire crepe blazer",
          "Sapphire matching tapered trousers",
          "Warm ivory silk camisole",
          "Golden pointed slingback heels",
          "Light camel leather belt",
        ],
        stylingNote:
          "A sapphire suit delivers navy-level gravitas with far more light in it, and the ivory-gold accents pull the look back to your warm base.",
      },
      {
        name: "Periwinkle Creative Meeting",
        occasion: "work",
        description:
          "A softer office option for brainstorm days and creative-industry dress codes.",
        pieces: [
          "Periwinkle oversized cotton shirt, tucked",
          "Light camel high-waisted pencil skirt",
          "Sand slingback flats",
          "Golden chain-link necklace",
          "Warm ivory cardigan over the shoulders",
        ],
        stylingNote:
          "Periwinkle from your complementary set gives gentle cool relief while staying clear and light enough for spring harmony.",
      },
      {
        name: "Berry Rooftop Date",
        occasion: "date",
        description:
          "Drinks-at-golden-hour outfit with a bolder color story than the usual little black dress.",
        pieces: [
          "Bright berry satin camisole",
          "Warm ivory wide-leg tailored trousers",
          "Golden heeled mules",
          "Light camel mini clutch",
          "Golden threader earrings",
        ],
        stylingNote:
          "Bright berry is your flirtiest complementary accent - clear and saturated, so it partners with golden warmth instead of muting it.",
      },
      {
        name: "Saffron Wedding Guest",
        occasion: "evening",
        description:
          "A joyful formal look for weddings and summer galas - the guest everyone asks about.",
        pieces: [
          "Golden pleated maxi dress with a cinched waist",
          "Coral silk shawl",
          "Golden block-heel sandals",
          "Warm ivory pearl-and-gold earrings",
          "Amber cocktail ring",
        ],
        stylingNote:
          "Head-to-toe warm gold with a coral accent is peak True Spring formalwear - maximum warmth, maximum clarity, zero black.",
      },
      {
        name: "Grass Court Weekend",
        occasion: "weekend",
        description:
          "Sporty-clean weekend dressing for brunch, errands, or actually watching tennis.",
        pieces: [
          "Warm ivory knit polo dress",
          "Jade lightweight bomber jacket",
          "Sand low-profile court sneakers",
          "Golden aviator sunglasses",
          "Light camel canvas sling bag",
        ],
        stylingNote:
          "The ivory base keeps things crisp while jade injects the living green that separates True Spring from every neutral-beige crowd.",
      },
    ],
    capsule: [
      "Warm ivory straight jeans",
      "Coral relaxed cotton tee",
      "Light camel single-breasted blazer",
      "Jade cotton camp-collar shirt",
      "Amber silk button-down blouse",
      "Sand tailored trousers",
      "Emerald satin wrap midi dress",
      "Warm poppy quilted puffer jacket",
      "Golden pendant necklace",
      "Sand leather ankle boots",
    ],
    faqs: [
      {
        question: "Can a True Spring wear black?",
        answer:
          "Black is not in the True Spring palette - it hardens your warm, clear coloring and creates shadows where you should glow. Your formal-depth substitutes are emerald and sapphire, which read just as dressed-up while keeping light in your face. If black is unavoidable, break it up with golden jewelry and a coral or amber layer at the neckline.",
      },
      {
        question: "What colors should a True Spring wear to a wedding?",
        answer:
          "Golden yellow, coral, emerald, and warm watermelon are your standout wedding-guest colors, ideally in satin, pleated, or flowing fabrics that catch light. Accessorize in gold rather than silver, and choose warm ivory over stark white for any neutral pieces. Skip dusty rose and charcoal grey - both are on your avoid list and will grey you out in photos.",
      },
      {
        question: "Can True Springs wear navy to work?",
        answer:
          "Classic dark navy is too heavy and cool for True Spring - swap it for sapphire, which does the same professional job with more clarity and life. An azure knit or a sapphire suit against warm ivory reads sharp without flattening your golden undertone. Reserve real navy for trousers far from your face if you must wear it.",
      },
      {
        question: "What neutrals should a True Spring build a wardrobe on?",
        answer:
          "Warm ivory, light camel, sand, and soft beige are your foundation neutrals - all carry the golden cast your skin needs. They replace white, grey, and black in every role: ivory instead of white shirts, camel instead of black blazers, sand instead of grey trousers. Muddy browns and dull olives look close but are muted, so check that every neutral still looks clean and warm.",
      },
    ],
  },
  "bright-spring": {
    displayName: "Bright Spring",
    season: "spring",
    subSeason: "bright",
    intro:
      "Bright Spring runs on maximum chroma with a warm engine underneath: your coloring can carry neon coral, electric yellow, and turquoise blue at full volume. The rule is clarity over everything - saturated warm brights near the face, clean light neutrals like warm white and buttermilk as the supporting cast, and nothing dusty, muddy, or greyed anywhere in the outfit. When in doubt, choose the louder color.",
    outfits: [
      {
        name: "Neon Coral Wake-Up Call",
        occasion: "casual",
        description:
          "Everyday brightness done wearably: one electric piece, clean neutrals everywhere else.",
        pieces: [
          "Neon coral boxy cotton tee",
          "Warm white wide-leg jeans",
          "Light tan leather sneakers",
          "Turquoise blue beaded phone strap",
          "Goldenrod canvas belt",
        ],
        stylingNote:
          "One full-saturation coral against warm white gives the crisp, high-chroma contrast Bright Spring needs - muted tones would visibly dull your face.",
      },
      {
        name: "Lime Bike-to-Brunch",
        occasion: "casual",
        description:
          "A sporty-casual combination with a green that most seasons cannot touch.",
        pieces: [
          "Bright lime green zip-up track jacket",
          "Warm white ribbed tank",
          "Light tan utility shorts",
          "Buttermilk canvas high-top sneakers",
          "Clear blue sports sunglasses",
        ],
        stylingNote:
          "Bright lime is a warm, clear green at maximum intensity - on your high-contrast coloring it looks fun rather than fluorescent.",
      },
      {
        name: "Electric Yellow Studio Hours",
        occasion: "casual",
        description:
          "Warm-brights athleisure for spin class, hot yoga, or looking alive before 8 a.m.",
        pieces: [
          "Electric yellow seamless leggings",
          "Warm pink cropped training top",
          "Warm white mesh trainers",
          "Soft camel oversized hoodie tied at the waist",
          "Bright aqua hair scrunchie",
        ],
        stylingNote:
          "Pairing two brights - electric yellow and warm pink - matches your natural intensity; a single grey piece would drag the whole look down.",
      },
      {
        name: "Turquoise Power Block",
        occasion: "work",
        description:
          "Color-blocked tailoring that makes you the most memorable person in the meeting for the right reasons.",
        pieces: [
          "Turquoise blue crepe blazer",
          "Warm white silk shell top",
          "Light tan cigarette trousers",
          "Buttermilk pointed-toe pumps",
          "Goldenrod enamel earrings",
        ],
        stylingNote:
          "A saturated turquoise jacket close to the face amplifies the clarity in Bright Spring eyes - your version of the navy blazer.",
      },
      {
        name: "Goldenrod Deadline Day",
        occasion: "work",
        description:
          "Polished separates with a rich yellow anchor for days you need visible confidence.",
        pieces: [
          "Goldenrod fine-knit sweater, half tucked",
          "Warm white pleated midi skirt",
          "Light tan block-heel slingbacks",
          "Clear blue leather laptop tote",
          "Warm white pearl studs",
        ],
        stylingNote:
          "Goldenrod carries your warm undertone at working-hours volume, and the clear blue bag adds the cool spark that keeps brights balanced.",
      },
      {
        name: "Warm Pink Standing Reservation",
        occasion: "date",
        description:
          "A dinner-date outfit that is bold, warm, and impossible to forget.",
        pieces: [
          "Warm pink satin slip dress",
          "Warm white cropped blazer over the shoulders",
          "Goldenrod strappy heels",
          "Bright lilac mini clutch",
        ],
        stylingNote:
          "Warm pink is your romance color - yellow-based enough to melt into golden skin, saturated enough to match your natural brightness.",
      },
      {
        name: "Hot Tomato Late Show",
        occasion: "evening",
        description:
          "Your red-dress moment: pure warm fire for parties, openings, and big birthdays.",
        pieces: [
          "Hot tomato stretch-crepe midi dress with a square neck",
          "Warm white tailored evening coat",
          "Buttermilk metallic heeled sandals",
          "Goldenrod chandelier earrings",
          "Poppy red lip",
        ],
        stylingNote:
          "Hot tomato at night does what black does for other seasons - commands the room - while keeping your face lit and warm.",
      },
      {
        name: "Bright Aqua Boardwalk",
        occasion: "weekend",
        description:
          "Vacation-grade color for weekends that involve water, ice cream, or both.",
        pieces: [
          "Bright aqua terry-cloth polo",
          "Warm white drawstring linen shorts",
          "Light tan leather sandals",
          "Clear orange woven beach bag",
          "Buttermilk bucket hat",
        ],
        stylingNote:
          "Aqua-plus-orange is a warm-cool bright pairing at equal intensity - the kind of confident color play Bright Spring was built for.",
      },
      {
        name: "Clear Orange Overshirt Days",
        occasion: "casual",
        description:
          "Transitional-weather layers with a citrus hit for crisp mornings.",
        pieces: [
          "Clear orange cotton twill overshirt",
          "Warm white slim jeans",
          "Buttermilk waffle-knit henley",
          "Light tan suede desert boots",
          "Turquoise blue carabiner keychain",
        ],
        stylingNote:
          "Clear orange stays luminous where rust or muddy brown would sink you - clarity is the line between Bright Spring and autumn.",
      },
      {
        name: "Grass Green Cold Snap",
        occasion: "casual",
        description:
          "Winter casual that refuses the all-black default: a vivid coat over creamy layers.",
        pieces: [
          "Vivid grass green wool-blend cocoon coat",
          "Buttermilk chunky turtleneck sweater",
          "Warm white straight-leg jeans",
          "Light tan lace-up combat boots",
          "Bright lemon knit beanie",
        ],
        stylingNote:
          "A saturated green coat keeps your chroma high in grey weather - Bright Springs fade fastest in winter neutrals, so the coat does the heavy lifting.",
      },
      {
        name: "Bright Peach Study Session",
        occasion: "casual",
        description:
          "Soft-volume brightness for library days, coffee shops, and casual hangs.",
        pieces: [
          "Bright peach oversized cotton crewneck sweatshirt",
          "Light tan wide-leg cargo trousers",
          "Warm white chunky sneakers",
          "Clear blue enamel pendant necklace",
        ],
        stylingNote:
          "Bright peach is your gentlest primary - still clear and warm, so even your relaxed clothes keep the glow going.",
      },
      {
        name: "Poppy Red Client Pitch",
        occasion: "work",
        description:
          "The highest-stakes-meeting outfit: commanding red tailoring, softened with warm neutrals.",
        pieces: [
          "Poppy red double-breasted blazer",
          "Buttermilk silk camisole",
          "Warm white tailored trousers",
          "Light tan pointed leather pumps",
          "Goldenrod slim watch",
        ],
        stylingNote:
          "Poppy red from your complementary set projects authority through saturation rather than darkness - Bright Spring power dressing in one jacket.",
      },
      {
        name: "Lemon Whiteboard Wednesday",
        occasion: "work",
        description:
          "Smart-casual for creative offices: sunny knit, sharp trousers, clean lines.",
        pieces: [
          "Bright lemon boxy short-sleeve knit top",
          "Light tan paperbag-waist trousers",
          "Warm white leather loafers",
          "Turquoise blue slim leather belt",
          "Buttermilk cardholder crossbody",
        ],
        stylingNote:
          "Bright lemon near the face echoes the golden light in your coloring, keeping you vivid under flat office lighting.",
      },
      {
        name: "Bright Lilac First Drinks",
        occasion: "date",
        description:
          "A cool-toned curveball for date night - playful, unexpected, still unmistakably you.",
        pieces: [
          "Bright lilac ruched mesh top",
          "Warm white high-waisted flared trousers",
          "Warm pink heeled mules",
          "Goldenrod hoop earrings",
          "Light tan mini shoulder bag",
        ],
        stylingNote:
          "Bright lilac works on Bright Spring because it is high-chroma - the intensity, not the temperature, is what keeps a cool tone in harmony with you.",
      },
      {
        name: "Neon Coral Reception Hours",
        occasion: "evening",
        description:
          "Wedding-guest maximalism: a saturated coral column with gold everywhere it counts.",
        pieces: [
          "Neon coral one-shoulder crepe maxi dress",
          "Goldenrod metallic clutch",
          "Warm white ankle-strap stiletto sandals",
          "Bright aqua cocktail ring",
          "Goldenrod ear cuffs",
        ],
        stylingNote:
          "Full-length neon coral is a chroma flex only Bright Spring can pull off at a formal event - the aqua ring adds the complementary spark.",
      },
      {
        name: "Bright Lime Farmers Market Kit",
        occasion: "weekend",
        description:
          "A Saturday-morning uniform that carries totes, kids, and dogs while staying loud and lovely.",
        pieces: [
          "Warm white denim overalls",
          "Bright lime green fitted baby tee",
          "Light tan platform sandals",
          "Clear orange bandana tied at the neck",
          "Buttermilk canvas market tote",
        ],
        stylingNote:
          "White denim is your best casual canvas - it lets lime and orange accents ring at full clarity without competing warmth.",
      },
    ],
    capsule: [
      "Warm white wide-leg jeans",
      "Neon coral boxy cotton tee",
      "Turquoise blue crepe blazer",
      "Goldenrod fine-knit sweater",
      "Light tan cigarette trousers",
      "Hot tomato stretch-crepe midi dress",
      "Vivid grass green wool-blend coat",
      "Buttermilk silk camisole",
      "Light tan leather sneakers",
      "Warm pink satin slip dress",
    ],
    faqs: [
      {
        question: "Can a Bright Spring wear black?",
        answer:
          "Bright Spring handles contrast better than the other spring seasons, but flat black still dulls your warm clarity - dark grey is on your avoid list for the same reason. If you want maximum drama, use hot tomato, poppy red, or turquoise at full saturation instead; they hit harder than black on you. When black is required, keep it minimal and add a bright layer or bold gold jewelry at the neckline.",
      },
      {
        question: "What colors should a Bright Spring wear to a wedding?",
        answer:
          "Go saturated: neon coral, warm pink, turquoise blue, or goldenrod in sleek fabrics like crepe and satin. Muted pastels will look washed out on you in photos, so choose the clearest version of any color you consider. Pair with warm white or gold accessories and skip anything dusty - muted olive and dull purple are explicit avoid colors.",
      },
      {
        question: "Are muted or pastel colors okay for a Bright Spring?",
        answer:
          "Softened, greyed colors are the one thing that genuinely fails on Bright Spring - they make vivid coloring look tired and sallow. Your light options are clear, not dusty: bright peach, buttermilk, and warm white instead of beige-pink, oatmeal, or grey. The test is simple: if the color looks like it has been washed fifty times, it is not yours.",
      },
      {
        question: "How does a Bright Spring wear brights to the office without overdoing it?",
        answer:
          "Use the one-hero rule: a single saturated piece - turquoise blazer, goldenrod knit, poppy red jacket - framed by warm white, buttermilk, or light tan. The neutral canvas keeps the look professional while the bright piece keeps your face alive. Two brights work for creative settings; three is a weekend outfit.",
      },
    ],
  },
  "light-summer": {
    displayName: "Light Summer",
    season: "summer",
    subSeason: "light",
    intro:
      "Light Summer dressing is about cool, airy softness - the palette of a hazy seaside morning. Your undertone is gently pink and your natural contrast is low, so outfits should stay light in value and cool in temperature: powder blue, rose quartz, and seafoam over pale ivory and silver grey, never black or dark navy. Think watercolor, not oil paint.",
    outfits: [
      {
        name: "Misty Blue Morning",
        occasion: "casual",
        description:
          "A soft everyday outfit that feels like a deep breath - cool pastels with zero hard edges.",
        pieces: [
          "Misty blue slub-cotton tee",
          "Pale ivory wide-leg jeans",
          "Silver grey suede sneakers",
          "Rose quartz pendant on a silver chain",
          "Light taupe canvas tote",
        ],
        stylingNote:
          "Misty blue against pale ivory mirrors Light Summer's low contrast, so nothing in the outfit shouts over your delicate cool coloring.",
      },
      {
        name: "Seafoam Slow Sunday",
        occasion: "casual",
        description:
          "Weekday-off softness for bookshops and bakery lines.",
        pieces: [
          "Seafoam oversized linen shirt",
          "Soft white ribbed tank",
          "Light taupe relaxed chino trousers",
          "Pale ivory low-profile sneakers",
          "Soft lavender knotted headband",
        ],
        stylingNote:
          "Cool greens like seafoam refresh pink-toned skin, where warm khaki or camel would turn it sallow.",
      },
      {
        name: "Lavender Reformer Hours",
        occasion: "casual",
        description:
          "Pastel athleisure for pilates, barre, and the smoothie afterward.",
        pieces: [
          "Soft lavender high-rise leggings",
          "Matching soft lavender scoop sports bra",
          "Soft white cropped zip hoodie",
          "Silver grey knit trainers",
          "Powder blue water bottle sleeve",
        ],
        stylingNote:
          "Tonal lavender keeps value light and temperature cool - the athleisure equivalent of the low-contrast rule that flatters you most.",
      },
      {
        name: "Powder Blue Quiet Authority",
        occasion: "work",
        description:
          "Soft-spoken tailoring that still runs the meeting.",
        pieces: [
          "Powder blue crepe blazer",
          "Soft white silk shell top",
          "Light taupe straight-leg trousers",
          "Pale ivory pointed-toe flats",
          "Silver bar-stud earrings",
        ],
        stylingNote:
          "A powder blue jacket does the professional work of navy at a value your light, cool coloring can actually support.",
      },
      {
        name: "Rose Quartz Review Day",
        occasion: "work",
        description:
          "A one-piece office solution with gentle color near the face.",
        pieces: [
          "Rose quartz knit midi dress with a polo collar",
          "Silver grey longline cardigan",
          "Light taupe slingback heels",
          "Pale periwinkle silk scarf on the bag handle",
        ],
        stylingNote:
          "Rose quartz repeats the natural pink in your skin, which reads as effortless polish rather than added color.",
      },
      {
        name: "Peach Pink Twilight Table",
        occasion: "date",
        description:
          "A dinner-date look in your warmest allowed pink - soft, luminous, close-up friendly.",
        pieces: [
          "Peach pink chiffon wrap blouse",
          "Pale ivory satin midi skirt",
          "Silver strappy kitten heels",
          "Rose quartz drop earrings",
        ],
        stylingNote:
          "Peach pink is the warm edge of your palette - flattering candlelight color without crossing into orange territory that fights your cool undertone.",
      },
      {
        name: "Lilac Standing Ovation",
        occasion: "evening",
        description:
          "Concert, gala, or anniversary dinner: pale drama in floaty fabric.",
        pieces: [
          "Lilac pleated chiffon maxi dress",
          "Silver metallic clutch",
          "Soft white satin heeled sandals",
          "Pale aqua crystal earrings",
          "Silver grey cashmere wrap",
        ],
        stylingNote:
          "Light Summer evening-wear goes lighter, not darker - lilac under low light keeps your face soft and luminous where black would erase it.",
      },
      {
        name: "Sky Blue Shoreline",
        occasion: "weekend",
        description:
          "A coastal-walk weekend outfit that works from boardwalk to cafe.",
        pieces: [
          "Sky blue cotton-cashmere crewneck sweater",
          "Soft white denim shorts",
          "Light taupe leather boat shoes",
          "Pale ivory canvas bucket hat",
          "Wisteria woven bracelet",
        ],
        stylingNote:
          "Sky blue picks up the cool light in your eyes and hair, keeping your natural coloring the focal point of the outfit.",
      },
      {
        name: "Powder Pink Errand Loop",
        occasion: "casual",
        description:
          "The softest possible jeans-and-knit formula for everyday wear.",
        pieces: [
          "Powder pink fine-gauge V-neck sweater",
          "Light taupe straight jeans",
          "Soft white leather sneakers",
          "Silver chain-link bracelet",
        ],
        stylingNote:
          "Powder pink with greyed taupe keeps chroma gentle - Light Summers look most expensive when nothing in the outfit is fully saturated.",
      },
      {
        name: "Silver Grey Frost Walk",
        occasion: "casual",
        description:
          "Cold-weather layers that stay light and cool while everyone else goes black.",
        pieces: [
          "Silver grey wool wrap coat",
          "Powder blue chunky turtleneck",
          "Pale ivory straight-leg corduroy trousers",
          "Light taupe shearling ankle boots",
          "Soft pink knit scarf",
        ],
        stylingNote:
          "Silver grey is your winter anchor - it gives coat-weight seriousness at a light value, with the soft pink scarf restoring color to your face.",
      },
      {
        name: "Mint Drizzle Layers",
        occasion: "casual",
        description:
          "A rainy-day outfit with fresh color under practical cover.",
        pieces: [
          "Light taupe hooded rain parka",
          "Mint crewneck sweatshirt",
          "Soft white slim jeans",
          "Silver grey waterproof Chelsea boots",
          "Pale periwinkle umbrella",
        ],
        stylingNote:
          "Cool mint under grey skies does what a bright can't on you - lifts the face without breaking Light Summer's muted-light harmony.",
      },
      {
        name: "Pale Periwinkle Panel Day",
        occasion: "work",
        description:
          "Presentation-ready separates in your most distinctive blue-violet.",
        pieces: [
          "Pale periwinkle silk button-down blouse",
          "Silver grey tailored wide-leg trousers",
          "Soft white leather loafers",
          "Light taupe structured tote",
          "Silver knot earrings",
        ],
        stylingNote:
          "Periwinkle sits exactly between blue and violet - a cool mid-tone that flatters Light Summer eyes under harsh conference lighting.",
      },
      {
        name: "Buttercream Client Coffee",
        occasion: "work",
        description:
          "Soft warm-neutral polish for approachable-professional days.",
        pieces: [
          "Buttercream boat-neck knit top",
          "Pale ivory pencil midi skirt",
          "Light taupe block-heel pumps",
          "Misty blue leather notebook folio",
          "Silver watch on a mesh strap",
        ],
        stylingNote:
          "Buttercream is your gentlest warm note - creamy without camel's heaviness, so it softens an ivory column instead of muddying it.",
      },
      {
        name: "Wisteria Wine Bar",
        occasion: "date",
        description:
          "Low-key romantic for a second date - soft violet with silvery light.",
        pieces: [
          "Wisteria satin camisole",
          "Silver grey draped midi skirt",
          "Soft white heeled mules",
          "Rose quartz cocktail ring",
          "Pale ivory cropped cardigan for the walk home",
        ],
        stylingNote:
          "Wisteria's greyed violet is pure Light Summer romance - saturated enough to feel special, soft enough to keep skin porcelain-smooth.",
      },
      {
        name: "Seafoam Garden Ceremony",
        occasion: "evening",
        description:
          "A wedding-guest look made for outdoor ceremonies and golden-hour photos.",
        pieces: [
          "Seafoam flutter-sleeve chiffon midi dress",
          "Pale ivory satin clutch",
          "Silver low-heel strappy sandals",
          "Soft lavender fascinator-style hair clip",
          "Silver tennis bracelet",
        ],
        stylingNote:
          "Seafoam photographs like sea glass on cool-toned skin - distinctive at a wedding without ever pulling focus from the couple.",
      },
      {
        name: "Pale Aqua Lake Weekend",
        occasion: "weekend",
        description:
          "Pack-light weekend dressing for lake houses and long porch afternoons.",
        pieces: [
          "Pale aqua waffle-knit henley",
          "Pale ivory linen drawstring trousers",
          "Light taupe suede slip-on sneakers",
          "Soft white canvas weekender bag",
          "Sky blue baseball cap",
        ],
        stylingNote:
          "Water-colored aquas and ivory keep the whole outfit at one soft value, the most relaxing thing you can do for Light Summer coloring.",
      },
    ],
    capsule: [
      "Pale ivory wide-leg jeans",
      "Powder blue crepe blazer",
      "Misty blue slub-cotton tee",
      "Rose quartz knit midi dress",
      "Light taupe straight-leg trousers",
      "Soft white silk shell top",
      "Silver grey wool wrap coat",
      "Lilac pleated chiffon maxi dress",
      "Soft white leather sneakers",
      "Seafoam oversized linen shirt",
    ],
    faqs: [
      {
        question: "Can a Light Summer wear black?",
        answer:
          "Black is on the Light Summer avoid list - it is far darker than anything in your natural coloring and creates a harsh frame that ages the face. Silver grey and light taupe do black's neutral job at a value you can carry, and lilac or powder blue handle evening drama. If black is mandatory, keep it away from your face and soften it with a pale scarf or silver jewelry.",
      },
      {
        question: "What colors should a Light Summer wear to a wedding?",
        answer:
          "Seafoam, lilac, powder blue, and rose quartz in chiffon or satin are ideal - light, cool, and dreamy in photographs. Choose silver accessories over gold and pale ivory over stark white for neutrals. Avoid bright orange and warm camel entirely; both clash with your cool pink undertone.",
      },
      {
        question: "Can Light Summers wear denim?",
        answer:
          "Yes - the trick is the wash. Light and mid-blue washes with a cool grey cast harmonize beautifully, as do pale ivory, soft white, and light taupe jeans. Skip inky dark-rinse and black denim, which introduce the heavy values your palette deliberately avoids.",
      },
      {
        question: "How is a Light Summer outfit different from a Light Spring one?",
        answer:
          "Both seasons wear light pastels, but Light Summer's lean cool and slightly greyed while Light Spring's lean warm and clear. Practically, you reach for powder pink, misty blue, and silver grey where a Light Spring reaches for peach, apricot, and camel. If a pastel looks sun-warmed, it is Spring's; if it looks sea-washed, it is yours.",
      },
    ],
  },
  "true-summer": {
    displayName: "True Summer",
    season: "summer",
    subSeason: "true",
    intro:
      "True Summer coloring is cool and muted - rose rather than gold, dusk rather than noon. Your outfits sing when every color has a whisper of grey in it: dusty rose, slate blue, sage, and mauve over soft grey and misty taupe. Loud saturation and hot warmth are the enemies; harmony, tone-on-tone layering, and gentle blues are the whole game.",
    outfits: [
      {
        name: "Dusty Rose Default",
        occasion: "casual",
        description:
          "The True Summer everyday answer: muted rose over grey-washed denim, comfortable and quietly beautiful.",
        pieces: [
          "Dusty rose relaxed cotton crewneck tee",
          "Slate gray straight-leg jeans",
          "Soft white leather sneakers",
          "Dusty lavender canvas tote",
          "Silver-toned huggie earrings",
        ],
        stylingNote:
          "Dusty rose repeats the cool pink of your natural flush, and slate denim keeps the base greyed rather than harsh.",
      },
      {
        name: "Sage Library Afternoon",
        occasion: "casual",
        description:
          "Muted greens and taupes for slow afternoons - reading, walking, wandering.",
        pieces: [
          "Sage oversized cotton shirt",
          "Misty taupe wide-leg trousers",
          "Dove grey suede loafers",
          "Soft white ribbed tank underneath",
          "Smoky blue crossbody bag",
        ],
        stylingNote:
          "Sage is green with the volume turned down - exactly the chroma level at which True Summer coloring looks most alive.",
      },
      {
        name: "Smoky Blue Long Run",
        occasion: "casual",
        description:
          "Cool-toned activewear that flatters mid-effort and post-shower alike.",
        pieces: [
          "Smoky blue seamless leggings",
          "Dusty lavender fitted training tank",
          "Soft grey lightweight running jacket",
          "Soft white cushioned running shoes",
          "Slate gray sweat-wicking headband",
        ],
        stylingNote:
          "Greyed blues and lavenders keep workout gear in your muted lane - neon activewear visibly fights True Summer's soft coloring.",
      },
      {
        name: "Slate Blue Chair of the Meeting",
        occasion: "work",
        description:
          "Your version of the power suit: cool, composed, impossible to ruffle.",
        pieces: [
          "Slate blue tailored blazer",
          "Slate blue matching straight trousers",
          "Soft white silk V-neck blouse",
          "Dove grey pointed-toe pumps",
          "Silver-toned slim bangle",
        ],
        stylingNote:
          "A slate blue suit carries navy's authority with the grey softness your palette requires - dark chocolate and pure black are your avoid colors for a reason.",
      },
      {
        name: "Mauve Monday Reset",
        occasion: "work",
        description:
          "Gentle color for the start of the week - knit dressing that still means business.",
        pieces: [
          "Mauve fine-knit polo sweater",
          "Misty taupe pleated midi skirt",
          "Soft grey slingback kitten heels",
          "Rosewood leather portfolio",
          "Soft white pearl studs",
        ],
        stylingNote:
          "Mauve is cool pink aged into elegance - it warms your face the way beige never will on a rose undertone.",
      },
      {
        name: "Rosewood Reservation for Two",
        occasion: "date",
        description:
          "A dinner look in your deepest romantic tone - soft drama without a hard edge.",
        pieces: [
          "Rosewood satin slip dress",
          "Soft grey fine-knit cardigan, worn open",
          "Silver strappy block heels",
          "Dusty rose lip and silver threader earrings",
        ],
        stylingNote:
          "Rosewood is as deep as True Summer should go for romance - muted enough to melt into your coloring under restaurant light.",
      },
      {
        name: "Dusty Teal After Eight",
        occasion: "evening",
        description:
          "Cocktail dressing in a moody, greyed teal that owns dim lighting.",
        pieces: [
          "Dusty teal draped-neck midi dress",
          "Slate gray velvet clutch",
          "Soft white satin heels",
          "Silver chandelier earrings",
          "Dusty lavender wrap for later",
        ],
        stylingNote:
          "Dusty teal gives evening depth through tone rather than darkness, keeping your face soft where black would carve shadows.",
      },
      {
        name: "Sand Dune Saturday",
        occasion: "weekend",
        description:
          "Neutral-on-neutral ease for coastal walks and long lunches.",
        pieces: [
          "Sand linen band-collar shirt",
          "Soft white relaxed jeans",
          "Misty taupe suede sandals",
          "Smoky blue lightweight scarf",
          "Dove grey straw tote",
        ],
        stylingNote:
          "Sand is your warm-adjacent neutral - greyed enough to stay cool-compatible, so it adds variety without breaking harmony.",
      },
      {
        name: "Dusty Lavender Field Trip",
        occasion: "casual",
        description:
          "A soft one-and-done dress for museum days and casual catch-ups.",
        pieces: [
          "Dusty lavender cotton shirtdress",
          "Soft white low-top sneakers",
          "Taupe woven belt",
          "Silver locket necklace",
        ],
        stylingNote:
          "Greyed lavender flatters cool-toned skin in daylight the way brights never can - saturation always reads louder outdoors.",
      },
      {
        name: "Moss Green November Layers",
        occasion: "casual",
        description:
          "Cold-weather casual built on muted green and grey - warm in fabric, cool in tone.",
        pieces: [
          "Moss green wool-blend overshirt jacket",
          "Soft grey merino turtleneck",
          "Misty taupe straight-leg corduroy trousers",
          "Dove grey lace-up leather boots",
          "Dusty rose knit beanie",
        ],
        stylingNote:
          "Moss green is your grounded winter color - it does olive's job without olive's yellow, which would sour a rose undertone.",
      },
      {
        name: "Soft Grey Drizzle Uniform",
        occasion: "casual",
        description:
          "Rain-ready layers that stay tonal and calm under grey skies.",
        pieces: [
          "Soft grey hooded rain trench",
          "Dusty teal crewneck sweater",
          "Slate gray slim jeans",
          "Misty taupe waterproof Chelsea boots",
          "Mauve compact umbrella",
        ],
        stylingNote:
          "True Summer is the season grey weather was made for - tonal greys with one muted accent look intentional, never gloomy, on you.",
      },
      {
        name: "Taupe Quarterly Review",
        occasion: "work",
        description:
          "Formal separates in warm-grey neutrals for high-stakes calendar days.",
        pieces: [
          "Taupe double-breasted blazer",
          "Soft white cotton poplin shirt",
          "Dove grey tailored cigarette trousers",
          "Slate blue leather tote",
          "Soft grey suede pumps",
        ],
        stylingNote:
          "Taupe is grey with a whisper of rose warmth - the most flattering formal neutral a True Summer can own.",
      },
      {
        name: "Faded Blue Workshop Day",
        occasion: "work",
        description:
          "Smart-casual for training days and creative sessions - soft blues doing the heavy lifting.",
        pieces: [
          "Faded blue chambray button-down shirt",
          "Misty taupe ponte trousers",
          "Soft white leather loafers",
          "Dusty rose fine-knit sweater over the shoulders",
          "Silver mesh-strap watch",
        ],
        stylingNote:
          "Faded blue from your complementary set is denim's dressier cousin - cool and softly worn-in, like your palette's entire philosophy.",
      },
      {
        name: "Muted Lilac Gallery Night",
        occasion: "date",
        description:
          "An art-opening date look - interesting, unhurried, quietly striking.",
        pieces: [
          "Muted lilac fine-rib knit top",
          "Slate gray satin bias-cut midi skirt",
          "Dove grey heeled ankle boots",
          "Soft white boxy cropped jacket",
          "Silver sculptural cuff",
        ],
        stylingNote:
          "Lilac against slate is a cool-on-cool pairing with just enough value contrast to be interesting - True Summer flirtation in fabric form.",
      },
      {
        name: "Dusty Rose Vow Renewal Guest",
        occasion: "evening",
        description:
          "Wedding-guest softness that photographs like a watercolor.",
        pieces: [
          "Dusty rose pleated chiffon maxi dress",
          "Soft grey satin wrap",
          "Silver low-block-heel sandals",
          "Mauve beaded clutch",
          "Soft white pearl drop earrings",
        ],
        stylingNote:
          "Muted rose in floaty pleats is peak True Summer formal - romantic depth achieved entirely through tone, not brightness.",
      },
      {
        name: "Smoky Blue Cabin Weekend",
        occasion: "weekend",
        description:
          "Cozy weekend-away layers for fireplaces and forest walks.",
        pieces: [
          "Smoky blue chunky shawl-collar cardigan",
          "Soft white waffle-knit henley",
          "Misty taupe relaxed jeans",
          "Moss green wool hiking socks peeking over dove grey boots",
          "Sage canvas duffel bag",
        ],
        stylingNote:
          "Layering smoky blue over soft white and taupe creates the misty, low-contrast gradient that makes True Summer coloring glow.",
      },
    ],
    capsule: [
      "Slate gray straight-leg jeans",
      "Dusty rose relaxed cotton tee",
      "Slate blue tailored blazer",
      "Soft white silk V-neck blouse",
      "Misty taupe wide-leg trousers",
      "Sage oversized cotton shirt",
      "Rosewood satin slip dress",
      "Soft grey hooded rain trench",
      "Dove grey suede loafers",
      "Mauve fine-knit polo sweater",
    ],
    faqs: [
      {
        question: "Can a True Summer wear black?",
        answer:
          "Pure black is one of True Summer's four avoid colors - its hard, saturated darkness overwhelms your soft, greyed coloring. Slate gray, slate blue, and dusty teal give you depth that harmonizes instead of dominating. For dress codes that insist, choose black in matte fabric, keep it below the neckline, and add a dusty rose or soft grey layer near the face.",
      },
      {
        question: "What colors should a True Summer wear to a wedding?",
        answer:
          "Dusty rose, mauve, dusty teal, and slate blue in chiffon, satin, or crepe are your wedding-guest sweet spot - muted tones photograph as timeless rather than trendy. Silver jewelry and soft white or grey accessories complete the cool story. Avoid bright red and warm mustard; both are explicit avoid colors that will clash with your rose undertone in photos.",
      },
      {
        question: "Can a True Summer wear brown?",
        answer:
          "Warm, chocolatey browns are on your avoid list, but cool greyed browns work: taupe, misty taupe, and rosewood all carry the grey cast your palette needs. The test is whether the brown looks closer to grey or to orange - grey-brown flatters, orange-brown fights you. Swap the classic brown boot for dove grey or taupe suede and everything harmonizes.",
      },
      {
        question: "What is the best denim wash for a True Summer?",
        answer:
          "Medium and faded blue washes with a grey cast are made for you - they are literally muted cool blue, the heart of your palette. Slate gray and soft white jeans extend the range. Avoid stark black denim and any wash with a yellow or orange tint, which introduces warmth your coloring cannot use.",
      },
    ],
  },
  "soft-summer": {
    displayName: "Soft Summer",
    season: "summer",
    subSeason: "cool",
    intro:
      "Soft Summer sits at the deep, cool end of the summer family - muted colors with real substance behind them. Your outfits work best in shadowed, grey-softened tones: medium navy, muted teal, cool plum, and raspberry over pewter and frosted white. Nothing warm, nothing neon; the goal is depth without hardness, like the ocean an hour before sunset.",
    outfits: [
      {
        name: "Muted Rose All Day",
        occasion: "casual",
        description:
          "A soft-but-substantial everyday outfit with rose warmth kept firmly cool.",
        pieces: [
          "Muted rose slouchy cotton sweater",
          "Soft navy straight-leg jeans",
          "Frosted white leather sneakers",
          "Pewter crossbody bag",
          "Silver small hoop earrings",
        ],
        stylingNote:
          "Muted rose over soft navy hits your ideal formula: greyed chroma with a real value step, so the look has depth without harshness.",
      },
      {
        name: "Teal Bookstore Circuit",
        occasion: "casual",
        description:
          "A rich muted blue-green for unhurried days in shirt-jacket weather.",
        pieces: [
          "Teal brushed-flannel overshirt",
          "Frosted white ribbed tank",
          "Steel gray relaxed chinos",
          "Soft navy canvas sneakers",
          "Cool slate beanie",
        ],
        stylingNote:
          "Teal is Soft Summer's showpiece - saturated enough to read as color, greyed enough to blend with cool, muted skin.",
      },
      {
        name: "Blue Violet Vinyasa",
        occasion: "casual",
        description:
          "Deep cool athleisure for yoga and everything on either side of it.",
        pieces: [
          "Blue violet high-rise leggings",
          "Muted teal cropped training tank",
          "Steel gray oversized zip hoodie",
          "Frosted white cushioned trainers",
          "Silver water bottle carabiner clip",
        ],
        stylingNote:
          "Blue violet and muted teal are cool tones at matched muted intensity, keeping even gym gear inside your greyed harmony.",
      },
      {
        name: "Medium Navy Signature Suit",
        occasion: "work",
        description:
          "The Soft Summer power look: navy that is deep but never black-hard.",
        pieces: [
          "Medium navy tailored blazer",
          "Medium navy matching wide-leg trousers",
          "Frosted white silk blouse",
          "Pewter pointed-toe pumps",
          "Silver slim-link watch",
        ],
        stylingNote:
          "Medium navy is your black substitute - the depth of a boardroom suit with the cool softness your muted coloring needs.",
      },
      {
        name: "Cool Spruce Deep Work",
        occasion: "work",
        description:
          "A grounded green for heads-down days and afternoon presentations.",
        pieces: [
          "Cool spruce fine-knit turtleneck",
          "Steel gray pleated trousers",
          "Soft navy suede loafers",
          "Pewter leather laptop tote",
          "Silver signet ring",
        ],
        stylingNote:
          "Cool spruce is forest green run through your grey filter - serious, wintry, and far more flattering on you than warm olive.",
      },
      {
        name: "Raspberry Reservation",
        occasion: "date",
        description:
          "Your boldest cool berry, deployed for date night.",
        pieces: [
          "Raspberry satin wrap blouse",
          "Soft navy tailored cigarette trousers",
          "Pewter strappy kitten heels",
          "Silver drop earrings",
          "Muted rose lip stain",
        ],
        stylingNote:
          "Raspberry is the loudest color a Soft Summer should own - a greyed berry that flushes the face beautifully without tipping into hot pink.",
      },
      {
        name: "Plum Hour Cocktails",
        occasion: "evening",
        description:
          "Dark-toned evening drama built from muted plum instead of default black.",
        pieces: [
          "Plum velvet midi slip dress",
          "Pewter metallic clutch",
          "Cool slate heeled sandals",
          "Silver crystal ear climbers",
          "Steel gray satin wrap",
        ],
        stylingNote:
          "Plum in light-absorbing velvet gives Soft Summer maximum evening depth while staying inside your cool, muted range.",
      },
      {
        name: "Ocean Blue Harbor Weekend",
        occasion: "weekend",
        description:
          "A breezy waterfront look for ferry rides and seafood lunches.",
        pieces: [
          "Ocean blue linen popover shirt",
          "Frosted white relaxed jeans",
          "Pewter leather slide sandals",
          "Cool slate canvas tote",
          "Silver rope-chain necklace",
        ],
        stylingNote:
          "Ocean blue against frosted white recreates your palette's core metaphor - deep cool water and sea mist - in two pieces.",
      },
      {
        name: "Cool Slate Studio Errands",
        occasion: "casual",
        description:
          "Tonal grey-blue dressing that makes running errands look art-directed.",
        pieces: [
          "Cool slate boxy sweatshirt",
          "Steel gray tapered joggers",
          "Frosted white chunky sneakers",
          "Muted berry knit scarf",
        ],
        stylingNote:
          "Slate-on-grey works on Soft Summer because your coloring is itself soft and cool - the muted berry scarf adds just enough blood-flow color.",
      },
      {
        name: "Cool Plum Frost Season",
        occasion: "casual",
        description:
          "Winter casual with a deep berry coat as the centerpiece.",
        pieces: [
          "Cool plum wool-blend cocoon coat",
          "Frosted white chunky cable turtleneck",
          "Soft navy straight corduroy trousers",
          "Pewter shearling-lined boots",
          "Steel gray knit gloves",
        ],
        stylingNote:
          "A cool plum coat delivers winter's required depth in a tone that flatters muted cool skin - your answer to yet another black puffer.",
      },
      {
        name: "Muted Teal Rain Delay",
        occasion: "casual",
        description:
          "Wet-weather layers that keep their color under cloud cover.",
        pieces: [
          "Soft navy waterproof rain shell",
          "Muted teal merino crewneck sweater",
          "Steel gray slim jeans",
          "Pewter rubber-sole Chelsea boots",
          "Blue violet umbrella",
        ],
        stylingNote:
          "Muted teal is made for overcast light - it holds its richness in grey weather where brights turn garish and warm tones die.",
      },
      {
        name: "Steel Gray Final Interview",
        occasion: "work",
        description:
          "Maximum-polish formalwear for the days that decide things.",
        pieces: [
          "Steel gray sheath dress",
          "Medium navy longline blazer",
          "Frosted white silk scarf at the neck",
          "Pewter block-heel pumps",
          "Silver stud earrings",
        ],
        stylingNote:
          "Steel gray plus medium navy is the most authoritative pairing in your palette - two cool deeps that read as gravitas, not gloom.",
      },
      {
        name: "Dusky Lilac Wednesday Standup",
        occasion: "work",
        description:
          "Softer office dressing for collaborative days - muted violet against grey.",
        pieces: [
          "Dusky lilac fine-gauge cardigan, buttoned as a top",
          "Cool slate tailored ankle trousers",
          "Frosted white leather loafers",
          "Soft navy structured shoulder bag",
          "Silver bar necklace",
        ],
        stylingNote:
          "Dusky lilac from your complementary set brings lightness to workwear while keeping every tone greyed and cool.",
      },
      {
        name: "Soft Blue First-Date Daylight",
        occasion: "date",
        description:
          "A daytime-date outfit that reads relaxed, open, and quietly put together.",
        pieces: [
          "Soft blue cashmere-blend V-neck sweater",
          "Frosted white satin midi skirt",
          "Pewter suede ankle boots",
          "Silver layered necklaces",
          "Steel gray mini shoulder bag",
        ],
        stylingNote:
          "Soft blue is your most disarming color - cool and gentle like your coloring, so you look approachable without losing polish.",
      },
      {
        name: "Blue Violet Black-Tie-Optional",
        occasion: "evening",
        description:
          "A formal wedding-guest or gala look in deep cool violet.",
        pieces: [
          "Blue violet bias-cut satin gown",
          "Silver metallic wrap",
          "Pewter strappy heeled sandals",
          "Frosted white opera-length gloves for drama",
          "Silver chandelier earrings",
        ],
        stylingNote:
          "Blue violet at floor length is Soft Summer's grandest gesture - jewel depth with a grey undercurrent that keeps your skin luminous.",
      },
      {
        name: "Pewter Trailhead Weekend",
        occasion: "weekend",
        description:
          "Hike-then-brunch layers in stone and spruce tones.",
        pieces: [
          "Cool spruce fleece quarter-zip",
          "Steel gray hiking trousers",
          "Pewter trail sneakers",
          "Frosted white thermal base layer",
          "Muted teal knit headband",
        ],
        stylingNote:
          "Spruce, slate, and pewter mirror a misty coastline - outdoor gear that happens to be a perfect Soft Summer palette exercise.",
      },
    ],
    capsule: [
      "Soft navy straight-leg jeans",
      "Medium navy tailored blazer",
      "Frosted white silk blouse",
      "Muted rose slouchy sweater",
      "Steel gray pleated trousers",
      "Teal brushed-flannel overshirt",
      "Plum velvet midi slip dress",
      "Cool plum wool-blend cocoon coat",
      "Pewter pointed-toe pumps",
      "Raspberry satin wrap blouse",
    ],
    faqs: [
      {
        question: "Can a Soft Summer wear black?",
        answer:
          "Black is not in the Soft Summer palette - it is harder and flatter than anything in your muted colouring and tends to hollow out the face. Medium navy, deep plum, and steel gray give you the same seriousness with a softness that matches your skin. Save true black for shoes and bags if you keep it at all.",
      },
      {
        question: "What colors should a Soft Summer wear to a wedding?",
        answer:
          "Raspberry, plum, muted teal, and blue violet in satin or velvet are your wedding-guest winners - deep enough for formality, muted enough to flatter. Pair with pewter or silver accessories and frosted white details rather than gold. Skip warm coral and warm gold, both explicit avoid colors that will fight your cool undertone in every photo.",
      },
      {
        question: "Can a Soft Summer wear bright colors at all?",
        answer:
          "Full-saturation brights overwhelm muted coloring, but you have deep-and-rich instead: raspberry, teal, and blue violet deliver impact through depth rather than neon intensity. If you love a bright, wear its greyed cousin - muted teal instead of turquoise, raspberry instead of hot pink. Distance helps too: a brighter shoe harms you far less than a bright top.",
      },
      {
        question: "What are the best neutrals for a Soft Summer capsule wardrobe?",
        answer:
          "Build on soft navy, steel gray, pewter, and frosted white - four cool neutrals that combine in any direction without a single clash. Soft navy replaces black for tailoring, frosted white replaces optic white for shirts, and pewter covers shoes and bags. Avoid rich camel and warm beige, which introduce a golden cast your rose-grey coloring cannot absorb.",
      },
    ],
  },
  "soft-autumn": {
    displayName: "Soft Autumn",
    season: "autumn",
    subSeason: "soft",
    intro:
      "Soft Autumn is warmth with the saturation gently sanded down - think dried flowers, worn leather, late-September light. Your undertone is warm but muted, so outfits should be built from gentle earth tones like sage, terracotta, muted gold, and dusty coral, layered over soft beige and muted camel. Anything neon, icy, or stark - bright white, jet black - will wear you instead of the other way around.",
    outfits: [
      {
        name: "Sage and Stoneware",
        occasion: "casual",
        description:
          "The Soft Autumn daily uniform: muted green over warm neutrals, comfortable as pottery in the hand.",
        pieces: [
          "Sage relaxed-fit crewneck sweater",
          "Muted camel straight-leg jeans",
          "Soft beige suede sneakers",
          "Soft bronze pendant necklace",
          "Warm taupe canvas tote",
        ],
        stylingNote:
          "Sage and camel share the same softened warmth as your skin and hair, so the outfit blends with you rather than outlining you.",
      },
      {
        name: "Dusty Coral Porch Coffee",
        occasion: "casual",
        description:
          "Gentle warm color for slow mornings and neighborhood wandering.",
        pieces: [
          "Dusty coral henley top",
          "Soft olive utility trousers",
          "Muted camel leather slide sandals",
          "Wheat woven-straw crossbody bag",
        ],
        stylingNote:
          "Dusty coral gives you the flush a bright coral promises but muted to your chroma level, so it flatters instead of overpowering.",
      },
      {
        name: "Mocha Trail Morning",
        occasion: "casual",
        description:
          "Earth-toned athleisure for trail walks, mat classes, and coffee after.",
        pieces: [
          "Mocha high-rise leggings",
          "Wheat ribbed longline sports bra",
          "Soft beige oversized half-zip fleece",
          "Warm taupe trail sneakers",
          "Moss green baseball cap",
        ],
        stylingNote:
          "Mocha and wheat keep activewear in your warm-muted zone - Soft Autumn looks athletic in earth tones, washed out in grey-and-neon kits.",
      },
      {
        name: "Warm Taupe Quiet Power",
        occasion: "work",
        description:
          "Tonal tailoring that reads competent and calm - the meeting outfit people trust.",
        pieces: [
          "Warm taupe relaxed-fit blazer",
          "Warm taupe matching tapered trousers",
          "Soft beige silk shell top",
          "Mocha pointed-toe leather flats",
          "Soft bronze small hoop earrings",
        ],
        stylingNote:
          "A warm taupe suit is Soft Autumn's charcoal - full professional weight delivered in the greyed-warm tones that flatter you.",
      },
      {
        name: "Olive Client Site Visit",
        occasion: "work",
        description:
          "Practical polish for days that mix desk and field.",
        pieces: [
          "Olive knit polo shirt",
          "Soft beige chino trousers",
          "Muted camel leather loafers",
          "Terracotta leather belt",
          "Warm taupe structured work bag",
        ],
        stylingNote:
          "Olive is one of your workhorse colors - a muted warm green that reads grounded and capable on softly warm coloring.",
      },
      {
        name: "Terracotta Table for Two",
        occasion: "date",
        description:
          "Warm, touchable romance - clay tones and soft fabric for dinner.",
        pieces: [
          "Terracotta satin wrap midi dress",
          "Soft beige cropped knit cardigan",
          "Soft bronze strappy heeled sandals",
          "Muted gold thin bangle stack",
        ],
        stylingNote:
          "Terracotta echoes the warmth in Soft Autumn skin under candlelight - it is your red-dress moment, translated to your chroma.",
      },
      {
        name: "Dusty Plum Gallery Evening",
        occasion: "evening",
        description:
          "Your version of dark evening drama - plum with the volume softened.",
        pieces: [
          "Dusty plum velvet slip dress",
          "Soft bronze metallic clutch",
          "Mocha suede heeled sandals",
          "Muted gold drop earrings",
          "Warm taupe pashmina wrap",
        ],
        stylingNote:
          "Dusty plum is the deepest Soft Autumn goes at night - rich in velvet, but muted enough to keep your face soft under low light.",
      },
      {
        name: "Wheat Field Weekend",
        occasion: "weekend",
        description:
          "Sun-faded neutrals for farm stands, flea markets, and long drives.",
        pieces: [
          "Wheat linen collarless shirt",
          "Soft olive relaxed shorts",
          "Soft beige leather sandals",
          "Muted camel straw sun hat",
          "Dusty coral bandana at the neck",
        ],
        stylingNote:
          "Wheat and olive look like late-summer fields - the exact warm, muted landscape your coloring belongs to.",
      },
      {
        name: "Moss Green Everyday Layers",
        occasion: "casual",
        description:
          "Transitional-weather layering with depth and zero harshness.",
        pieces: [
          "Moss green corduroy shirt jacket",
          "Soft beige waffle-knit henley",
          "Muted camel wide-leg jeans",
          "Warm taupe suede desert boots",
          "Soft rust knit scarf",
        ],
        stylingNote:
          "Moss over beige with a soft rust accent stacks three muted warms at different depths - the layered-forest effect Soft Autumn wears best.",
      },
      {
        name: "Muted Camel Deep Winter Walk",
        occasion: "casual",
        description:
          "Cold-season casual that keeps its warmth when the light goes flat.",
        pieces: [
          "Muted camel wool wrap coat",
          "Dusty plum chunky turtleneck sweater",
          "Soft olive straight corduroy trousers",
          "Mocha shearling-lined leather boots",
          "Soft beige cable-knit beanie",
        ],
        stylingNote:
          "A camel coat over dusty plum keeps winter dressing warm-toned and muted - jet black outerwear is the fastest way to grey out a Soft Autumn face.",
      },
      {
        name: "Soft Rust Farmers Market",
        occasion: "casual",
        description:
          "An easy dress-and-sneakers combination with autumn warmth in spring weight.",
        pieces: [
          "Soft rust cotton midi shirtdress",
          "Soft beige low-top canvas sneakers",
          "Muted gold layered necklaces",
          "Warm taupe woven belt",
        ],
        stylingNote:
          "Soft rust is terracotta's quieter sibling - warm enough to light your face, muted enough for bright daylight.",
      },
      {
        name: "Mocha Board Presentation",
        occasion: "work",
        description:
          "The most formal Soft Autumn outfit: deep warm brown treated like a suit.",
        pieces: [
          "Mocha crepe blazer",
          "Wheat silk button-down blouse",
          "Mocha tailored straight trousers",
          "Warm taupe leather pumps",
          "Soft bronze slim watch",
        ],
        stylingNote:
          "Mocha head-to-toe delivers boardroom gravity through warm depth - your alternative to the black suit that would swallow you.",
      },
      {
        name: "Muted Gold Creative Review",
        occasion: "work",
        description:
          "Warm, approachable smart-casual for collaborative days.",
        pieces: [
          "Muted gold fine-knit sweater",
          "Warm taupe pleated midi skirt",
          "Soft beige suede block-heel boots",
          "Soft olive leather tote",
          "Soft bronze stud earrings",
        ],
        stylingNote:
          "Muted gold near the face reflects your golden-but-gentle undertone back at you, which is why it reads as instant good lighting.",
      },
      {
        name: "Dusty Aqua Long Lunch Date",
        occasion: "date",
        description:
          "A daytime date look with a rare cool note, kept soft and sun-washed.",
        pieces: [
          "Dusty aqua flowy blouse with tie cuffs",
          "Soft beige bias-cut satin midi skirt",
          "Muted camel heeled mules",
          "Muted gold locket necklace",
          "Warm taupe mini shoulder bag",
        ],
        stylingNote:
          "Dusty aqua from your complementary set cools the outfit without chilling your skin - the mutedness keeps it in family.",
      },
      {
        name: "Soft Bronze Harvest Wedding",
        occasion: "evening",
        description:
          "A wedding-guest look built for vineyard and barn venues - burnished, warm, photogenic at dusk.",
        pieces: [
          "Soft bronze satin cowl-neck maxi dress",
          "Dusty coral silk shawl",
          "Muted gold block-heel sandals",
          "Muted gold sculptural earrings",
          "Mocha suede clutch",
        ],
        stylingNote:
          "Metallic-adjacent bronze satin catches golden-hour light exactly the way your coloring does - formalwear as camouflage in the best sense.",
      },
      {
        name: "Olive Orchard Weekend",
        occasion: "weekend",
        description:
          "Apple-picking-grade comfort with a fully coordinated palette.",
        pieces: [
          "Soft olive quilted liner jacket",
          "Dusty coral brushed-cotton flannel shirt",
          "Wheat straight-leg jeans",
          "Mocha leather lace-up boots",
          "Sage knit beanie",
        ],
        stylingNote:
          "Olive, coral, and wheat pull three muted warms into one easy gradient - weekend proof that Soft Autumn never needs high contrast to look finished.",
      },
    ],
    capsule: [
      "Muted camel straight-leg jeans",
      "Sage relaxed crewneck sweater",
      "Warm taupe relaxed-fit blazer",
      "Soft beige silk shell top",
      "Olive knit polo shirt",
      "Terracotta satin wrap midi dress",
      "Muted camel wool wrap coat",
      "Mocha tailored straight trousers",
      "Soft beige suede sneakers",
      "Dusty coral henley top",
    ],
    faqs: [
      {
        question: "Can a Soft Autumn wear black?",
        answer:
          "Jet black is an explicit Soft Autumn avoid color - its cold, hard saturation flattens your gentle warmth and casts shadows under the chin. Mocha, deep olive-toned pieces, and dusty plum give you darkness that stays in harmony. If black is unavoidable, keep it in bottoms or shoes and put wheat, sage, or dusty coral next to your face.",
      },
      {
        question: "What colors should a Soft Autumn wear to a wedding?",
        answer:
          "Terracotta, soft bronze, dusty plum, and muted gold in satin or velvet photograph beautifully on warm-muted coloring, especially at outdoor and golden-hour venues. Choose muted gold or bronze accessories over silver, and soft beige over bright white for any light pieces. Avoid cool fuchsia and bright white - both are avoid colors that will make you look pale beside them.",
      },
      {
        question: "Can a Soft Autumn wear white shirts?",
        answer:
          "Bright optic white is on your avoid list - it is starker than anything in your coloring. Swap it for soft beige, wheat, or warm cream shirting, which do the same clean-canvas job while flattering your muted warmth. In photos the difference is dramatic: soft beige lights your face, bright white bleaches it.",
      },
      {
        question: "Is Soft Autumn just neutrals, or can I wear real color?",
        answer:
          "You have plenty of real color - terracotta, dusty coral, muted gold, sage, dusty plum - it is simply color with the shine rubbed off. The rule is muted, not colorless: a full terracotta dress is very much a statement on you. What fails is saturation, not color itself, so buy the rich-but-dusty version of every hue you love.",
      },
    ],
  },
  "true-autumn": {
    displayName: "True Autumn",
    season: "autumn",
    subSeason: "true",
    intro:
      "True Autumn is the golden heart of the warm seasons: rich, spiced, and unmistakably earthy. Your coloring carries saturated warmth - copper, rust, goldenrod, warm moss - grounded by camel and golden beige, with not a cool or icy tone in sight. Dress like October at full blaze: deep warm color, natural textures like suede and wool, and gold on every metal.",
    outfits: [
      {
        name: "Rust Never Sleeps",
        occasion: "casual",
        description:
          "The definitive True Autumn casual: rust knitwear over warm denim tones.",
        pieces: [
          "Rust chunky ribbed cardigan",
          "Golden beige straight-leg jeans",
          "Camel suede chukka boots",
          "Warm olive canvas tote",
          "Bronze coin pendant",
        ],
        stylingNote:
          "Rust is your palette's north star - a red with earth in it that makes golden-toned skin look sunlit year round.",
      },
      {
        name: "Warm Moss Trail Cafe",
        occasion: "casual",
        description:
          "Forest tones for daytime plans that involve a lot of walking.",
        pieces: [
          "Warm moss waffle-knit henley",
          "Camel utility trousers with a cargo pocket",
          "Golden beige retro hiking sneakers",
          "Spice knit scarf looped once",
        ],
        stylingNote:
          "Warm moss carries yellow at its core, so it deepens your warmth where a cool forest green would mute it.",
      },
      {
        name: "Copper Kettle Conditioning",
        occasion: "casual",
        description:
          "Warm-metal athleisure for strength class and smoothie-bar loitering.",
        pieces: [
          "Copper ribbed seamless leggings",
          "Warm olive cropped training tank",
          "Golden beige oversized crewneck sweatshirt",
          "Camel knit sneakers",
          "Bronze hair claw",
        ],
        stylingNote:
          "Copper activewear does what black leggings never will for True Autumn - reflects heat and gold back into your skin mid-workout.",
      },
      {
        name: "Camel Corner Office",
        occasion: "work",
        description:
          "The tailored camel suit: True Autumn's answer to every navy-and-charcoal dress code.",
        pieces: [
          "Camel double-breasted blazer",
          "Camel matching wide-leg trousers",
          "Warm ivory silk blouse",
          "Sienna leather pointed pumps",
          "Bronze link bracelet",
        ],
        stylingNote:
          "Camel tailoring reads as expensive on you because it matches your undertone exactly - authority achieved through harmony, not darkness.",
      },
      {
        name: "Warm Olive Ops Day",
        occasion: "work",
        description:
          "Utility-smart workwear for busy, on-your-feet days.",
        pieces: [
          "Warm olive knit blazer",
          "Golden beige tailored chinos",
          "Camel leather loafers",
          "Goldenrod fine-knit shell top",
          "Warm taupe leather satchel",
        ],
        stylingNote:
          "Olive-and-goldenrod is a True Autumn signature pairing - deep warm green sharpened by your palette's richest yellow.",
      },
      {
        name: "Sienna Slow Dinner",
        occasion: "date",
        description:
          "Earthy-red romance in touchable fabric for a long dinner.",
        pieces: [
          "Sienna slinky knit midi dress",
          "Camel suede heeled ankle boots",
          "Bronze sculptural cuff bracelet",
          "Warm taupe cropped shearling jacket for the walk",
        ],
        stylingNote:
          "Sienna is warm red-brown at date-night depth - it flushes your face like firelight while staying entirely in your earth family.",
      },
      {
        name: "Bronze Age Evening",
        occasion: "evening",
        description:
          "Metallic warmth for parties: your alternative to the sequin-black default.",
        pieces: [
          "Bronze liquid-satin cowl-neck slip dress",
          "Spice velvet clutch",
          "Camel strappy heeled sandals",
          "Goldenrod statement drop earrings",
          "Warm ivory faux-fur stole",
        ],
        stylingNote:
          "Metallic bronze is True Autumn's evening superpower - it does the light-catching work of sequins in a tone that is literally your coloring.",
      },
      {
        name: "Pumpkin Patch Weekend",
        occasion: "weekend",
        description:
          "Peak-autumn weekend dressing - flannel-soft, cider-warm, photograph-ready.",
        pieces: [
          "Pumpkin brushed-flannel shirt, worn open",
          "Warm ivory thermal henley",
          "Camel straight-leg jeans",
          "Warm olive lace-up leather boots",
          "Rust knit beanie",
        ],
        stylingNote:
          "Pumpkin over ivory with olive boots is the whole season in one outfit - warm, saturated, and earthed, exactly like your coloring.",
      },
      {
        name: "Goldenrod Sidewalk Standard",
        occasion: "casual",
        description:
          "A simple, saturated sweater-and-denim formula for everyday errands.",
        pieces: [
          "Goldenrod chunky crewneck sweater",
          "Warm taupe relaxed jeans",
          "Camel leather low-top sneakers",
          "Bronze small hoop earrings",
        ],
        stylingNote:
          "Goldenrod is your face-lighting yellow - deep and honeyed, not lemony, so it amplifies rather than argues with golden skin.",
      },
      {
        name: "Spice Route Cold Front",
        occasion: "casual",
        description:
          "Deep-winter casual that stays blazing warm in tone as well as fabric.",
        pieces: [
          "Spice wool-blend teddy coat",
          "Warm ivory chunky turtleneck",
          "Warm olive straight corduroy trousers",
          "Camel shearling-lined boots",
          "Goldenrod chunky knit scarf",
        ],
        stylingNote:
          "A spice-toned coat keeps True Autumn saturated when winter light drains everyone else - your warmth is the whole point, so never bury it in black.",
      },
      {
        name: "Vermilion Market Sprint",
        occasion: "casual",
        description:
          "One vivid warm-red piece worn casually - the fastest possible upgrade to jeans.",
        pieces: [
          "Vermilion boxy cotton overshirt",
          "Golden beige wide-leg jeans",
          "Warm ivory canvas sneakers",
          "Camel crossbody saddle bag",
        ],
        stylingNote:
          "Vermilion is your loudest allowed red - orange-blooded and alive, it proves True Autumn does bold without ever going cool.",
      },
      {
        name: "Warm Saffron Keynote",
        occasion: "work",
        description:
          "Statement workwear for stage days, panels, and being unmissable on video calls.",
        pieces: [
          "Warm saffron silk button-down blouse",
          "Warm taupe high-waisted tailored trousers",
          "Camel slingback heels",
          "Bronze slim-frame glasses chain",
          "Sienna leather portfolio",
        ],
        stylingNote:
          "Saffron near the face reads as confidence and warmth on camera - the spiced yellow only truly warm seasons can wear at full strength.",
      },
      {
        name: "Golden Beige Friday Wind-Down",
        occasion: "work",
        description:
          "Soft-tailored end-of-week dressing that still holds a client meeting.",
        pieces: [
          "Golden beige fine-knit polo sweater",
          "Warm moss pleated midi skirt",
          "Camel suede block-heel boots",
          "Bronze watch on a leather strap",
          "Warm ivory structured tote",
        ],
        stylingNote:
          "Golden beige is your true neutral - it does grey's quiet-canvas job while feeding warmth up into your face all day.",
      },
      {
        name: "Sunflower Terrace Date",
        occasion: "date",
        description:
          "A warm, open-hearted daytime date look for terraces and long walks.",
        pieces: [
          "Sunflower wrap-front midi dress",
          "Camel woven leather flat sandals",
          "Bronze beaded anklet",
          "Warm olive denim jacket for the evening chill",
          "Golden beige mini shoulder bag",
        ],
        stylingNote:
          "Sunflower yellow is pure True Autumn joy - saturated, honeyed, and disarming, it reads as golden-hour light even at noon.",
      },
      {
        name: "Copper Vineyard Wedding",
        occasion: "evening",
        description:
          "A wedding-guest outfit made for autumn ceremonies and candlelit receptions.",
        pieces: [
          "Copper pleated lame maxi dress",
          "Warm ivory satin wrap",
          "Bronze metallic block-heel sandals",
          "Rust velvet clutch",
          "Goldenrod cocktail ring",
        ],
        stylingNote:
          "Copper pleats move like fire in reception light - deep metallic warmth that flatters True Autumn skin from ceremony to last dance.",
      },
      {
        name: "Warm Moss Cabin Weekend",
        occasion: "weekend",
        description:
          "Slow-weekend layers for cabins, campfires, and gravel-road drives.",
        pieces: [
          "Warm moss shawl-collar cardigan",
          "Golden beige waffle-knit thermal top",
          "Sienna straight-leg jeans",
          "Camel wool-lined moc-toe boots",
          "Pumpkin plaid blanket scarf",
        ],
        stylingNote:
          "Moss, sienna, and pumpkin layered together read like the forest floor in October - True Autumn dressing at its most literal and most flattering.",
      },
    ],
    capsule: [
      "Camel double-breasted blazer",
      "Rust chunky ribbed cardigan",
      "Warm ivory silk blouse",
      "Golden beige straight-leg jeans",
      "Warm olive knit blazer",
      "Goldenrod chunky crewneck sweater",
      "Sienna slinky knit midi dress",
      "Spice wool-blend teddy coat",
      "Camel suede chukka boots",
      "Warm taupe tailored trousers",
    ],
    faqs: [
      {
        question: "Can a True Autumn wear black?",
        answer:
          "Pure black is an explicit True Autumn avoid color - it is cool and flat where you are warm and rich, and it drains the gold from your face. Deep warm substitutes like sienna, spice, and dark olive-toned pieces give you evening depth that harmonizes. If black is required, keep it below the waist and anchor the neckline in goldenrod, rust, or warm ivory.",
      },
      {
        question: "What colors should a True Autumn wear to a wedding?",
        answer:
          "Copper, bronze, sienna, and warm saffron in satin, pleats, or velvet are spectacular on you, especially for fall and golden-hour ceremonies. Keep every metal gold or bronze and every neutral warm - warm ivory instead of bright white, which is on your avoid list. Cool pink and cool navy are the other avoid colors to skip when browsing guest dresses.",
      },
      {
        question: "What jewelry metals suit a True Autumn?",
        answer:
          "Gold, bronze, and copper - the warmer and more burnished, the better. Silver and white gold read icy against your golden undertone and visibly cool your face. Matte and antiqued gold finishes harmonize even better than high-shine, echoing the earthy texture of your palette.",
      },
      {
        question: "Can True Autumns wear jeans, given denim is a cool blue?",
        answer:
          "Yes - choose warm-influenced denim: golden-beige, camel, sienna-tinted, and warm taupe jeans are effectively neutrals in your palette. If you want blue denim, pick washes with a soft, warm cast rather than icy or inky rinses, and balance them with rust or goldenrod on top. Your best trousers overall are simply non-denim earth tones.",
      },
    ],
  },
  "deep-autumn": {
    displayName: "Deep Autumn",
    season: "autumn",
    subSeason: "deep",
    intro:
      "Deep Autumn is warmth taken into shadow: espresso, mahogany, forest green, and burnt umber, worn with real contrast and quiet force. Your coloring is dark and warm, so it can anchor colors that would swallow lighter seasons - the mistake is going pale or icy, not going dark. Build outfits like a wood-paneled library: deep, rich, layered, always warm at the core.",
    outfits: [
      {
        name: "Espresso Shot Errands",
        occasion: "casual",
        description:
          "The Deep Autumn daily default: near-black browns with far more life than actual black.",
        pieces: [
          "Espresso oversized cotton hoodie",
          "Deep camel straight-leg jeans",
          "Dark chestnut leather sneakers",
          "Dark amber resin pendant",
          "Rich olive canvas tote",
        ],
        stylingNote:
          "Espresso does everything black does for other seasons while keeping the warm depth your coloring is built on.",
      },
      {
        name: "Forest Green Field Notes",
        occasion: "casual",
        description:
          "Deep green layers for crisp days spent mostly outside.",
        pieces: [
          "Forest green heavyweight flannel shirt",
          "Walnut carpenter trousers",
          "Deep camel suede boots",
          "Espresso leather belt",
          "Dark bronze knit beanie",
        ],
        stylingNote:
          "Forest green at full depth flatters Deep Autumn's high natural contrast - the same green would flatten a soft or light season.",
      },
      {
        name: "Maroon Miles Run Club",
        occasion: "casual",
        description:
          "Dark, rich athleisure that looks as strong as it performs.",
        pieces: [
          "Maroon seamless leggings",
          "Deep olive fitted training tank",
          "Espresso zip-through track jacket",
          "Dark chestnut trail running shoes",
          "Dark amber hair claw clip",
        ],
        stylingNote:
          "Maroon and deep olive give workout gear the shadowed warmth your coloring demands - pale pastel kits are what wash you out.",
      },
      {
        name: "Deep Mahogany Managing Partner",
        occasion: "work",
        description:
          "Dark warm tailoring with the presence of a black suit and twice the flattery.",
        pieces: [
          "Deep mahogany wool blazer",
          "Deep mahogany matching tapered trousers",
          "Deep camel silk blouse",
          "Espresso leather pointed pumps",
          "Dark bronze cuff bracelet",
        ],
        stylingNote:
          "Mahogany tailoring reads as authority in your exact temperature - dark enough for the boardroom, warm enough to keep your face lit.",
      },
      {
        name: "Deep Olive Site Walkthrough",
        occasion: "work",
        description:
          "Rugged-smart workwear for days split between desk, site, and street.",
        pieces: [
          "Deep olive knit blazer",
          "Walnut tailored chinos",
          "Dark chestnut leather loafers",
          "Deep camel fine-gauge turtleneck",
          "Espresso structured briefcase bag",
        ],
        stylingNote:
          "Deep olive and walnut sit at matching depth and warmth, building the tonal richness that makes Deep Autumn workwear look deliberate.",
      },
      {
        name: "Deep Rust Reservation",
        occasion: "date",
        description:
          "Smoldering dinner-date warmth in one sweeping color.",
        pieces: [
          "Deep rust satin midi slip dress",
          "Espresso cropped leather jacket",
          "Dark bronze strappy heeled sandals",
          "Dark amber teardrop earrings",
        ],
        stylingNote:
          "Deep rust is your ember red - saturated warmth at shadow depth that makes dark eyes and warm skin look lit from within.",
      },
      {
        name: "Deep Terracotta Late Reception",
        occasion: "evening",
        description:
          "Evening drama in burnished clay - unexpected, elegant, unmistakably warm.",
        pieces: [
          "Deep terracotta velvet column gown",
          "Dark bronze metallic clutch",
          "Espresso satin heeled sandals",
          "Dark amber statement cocktail ring",
          "Deep camel cashmere evening wrap",
        ],
        stylingNote:
          "Terracotta deepened to velvet is Deep Autumn's black-tie flex - it absorbs light like black but glows warm where black goes dead on you.",
      },
      {
        name: "Burnt Umber Sunday Roast",
        occasion: "weekend",
        description:
          "Slow-weekend comfort with pub-lunch polish.",
        pieces: [
          "Burnt umber shawl-collar cardigan",
          "Deep camel brushed-cotton shirt",
          "Deep olive straight-leg jeans",
          "Walnut leather Chelsea boots",
          "Maroon wool scarf",
        ],
        stylingNote:
          "Umber over camel and olive stacks three warm depths into one gradient - the layered, roasted look Deep Autumn owns outright.",
      },
      {
        name: "Dark Chestnut School Run",
        occasion: "casual",
        description:
          "Fast, practical, and rich-toned for full-speed mornings.",
        pieces: [
          "Dark chestnut quilted vest",
          "Deep camel merino crewneck sweater",
          "Espresso slim jeans",
          "Rich olive leather sneakers",
          "Dark bronze aviator sunglasses",
        ],
        stylingNote:
          "Chestnut and espresso keep even thrown-on outfits inside your warm-dark harmony, so speed never costs you polish.",
      },
      {
        name: "Deep Moss Storm Layers",
        occasion: "casual",
        description:
          "Foul-weather casual that keeps its richness under heavy skies.",
        pieces: [
          "Deep moss waxed-cotton field jacket",
          "Dark amber chunky turtleneck sweater",
          "Walnut straight corduroy trousers",
          "Espresso lug-sole rain boots",
          "Deep camel wool watch cap",
        ],
        stylingNote:
          "Waxed deep moss is your rain shell answer - the darker the weather, the better your shadowed palette performs.",
      },
      {
        name: "Dark Amber Study Hall",
        occasion: "casual",
        description:
          "A warm, bookish knit-and-denim look for cafes and libraries.",
        pieces: [
          "Dark amber ribbed mock-neck sweater",
          "Espresso wide-leg jeans",
          "Dark chestnut suede loafers",
          "Deep olive corduroy baseball cap",
        ],
        stylingNote:
          "Dark amber near the face is Deep Autumn's honey trap - it pulls the gold out of dark eyes and warm skin instantly.",
      },
      {
        name: "Maroon Contract Signing",
        occasion: "work",
        description:
          "High-stakes formalwear with a dark red center of gravity.",
        pieces: [
          "Maroon crepe sheath dress",
          "Espresso longline blazer",
          "Dark chestnut leather pumps",
          "Dark bronze slim watch",
          "Deep camel leather portfolio",
        ],
        stylingNote:
          "Maroon carries the seriousness of navy in a warm-blooded register - Deep Autumn power dressing without a cool tone in sight.",
      },
      {
        name: "Rich Olive Video Call Uniform",
        occasion: "work",
        description:
          "Camera-tested smart-casual that flatters on screen and in person.",
        pieces: [
          "Rich olive fine-knit polo sweater",
          "Espresso tailored ankle trousers",
          "Walnut suede desert boots",
          "Dark amber beaded bracelet",
          "Deep camel structured tote",
        ],
        stylingNote:
          "Rich olive reads warm and grounded on camera, where black turns to a void and pale tones glare - your webcam advantage.",
      },
      {
        name: "Wine-Dark Jazz Bar Date",
        occasion: "date",
        description:
          "A low-lit-venue look with texture doing half the talking.",
        pieces: [
          "Deep terracotta silk camisole",
          "Espresso high-waisted velvet trousers",
          "Dark bronze heeled mules",
          "Dark amber layered necklaces",
          "Deep camel wool overcoat draped on shoulders",
        ],
        stylingNote:
          "Terracotta against espresso velvet gives candlelight something to catch - warm contrast at depth is Deep Autumn's seduction setting.",
      },
      {
        name: "Forest Green Winter Wedding Guest",
        occasion: "evening",
        description:
          "A cold-season wedding-guest look with jewel depth and zero black.",
        pieces: [
          "Forest green satin wrap gown",
          "Dark bronze envelope clutch",
          "Espresso suede heeled sandals",
          "Dark amber chandelier earrings",
          "Maroon velvet shawl",
        ],
        stylingNote:
          "Forest green satin at floor length is jewel-tone formality in your warm register - richer in photos than any black dress on you.",
      },
      {
        name: "Walnut Trailhead Weekend",
        occasion: "weekend",
        description:
          "Hike-ready layers in timber tones for weekends that end muddy and happy.",
        pieces: [
          "Walnut fleece-lined shacket",
          "Deep moss thermal henley",
          "Dark chestnut hiking trousers",
          "Espresso waterproof hiking boots",
          "Deep rust knit headband",
        ],
        stylingNote:
          "Timber browns and deep moss make trail gear look intentional - Deep Autumn is the rare season whose palette ships with the landscape.",
      },
    ],
    capsule: [
      "Espresso slim jeans",
      "Deep mahogany wool blazer",
      "Deep camel silk blouse",
      "Forest green heavyweight flannel shirt",
      "Walnut tailored chinos",
      "Maroon crepe sheath dress",
      "Deep moss waxed-cotton field jacket",
      "Dark amber ribbed mock-neck sweater",
      "Dark chestnut leather Chelsea boots",
      "Deep rust satin midi slip dress",
    ],
    faqs: [
      {
        question: "Can a Deep Autumn wear black?",
        answer:
          "Deep Autumn handles darkness better than any other autumn, but black itself is cool and flat next to your warm depth - espresso and dark chocolate are your palette's actual near-blacks. They photograph richer against warm skin and dark hair, with none of black's harsh shadow. If you keep black, treat it like a bottom-half neutral and warm the neckline with dark amber or deep camel.",
      },
      {
        question: "What colors should a Deep Autumn wear to a wedding?",
        answer:
          "Forest green, deep terracotta, maroon, and deep rust in satin or velvet are your strongest guest colors - jewel-level depth in a warm register that flatters at both daytime and candlelit receptions. Bronze and dark gold accessories finish the look better than silver. Skip icy pink and pure white, both explicit avoid colors that will look chalky against your coloring.",
      },
      {
        question: "What is the difference between Deep Autumn and Deep Winter outfits?",
        answer:
          "Both seasons wear dark, high-impact colors, but Deep Autumn's darks are warm - espresso, mahogany, forest green - while Deep Winter's are cool - black, deep navy, wine. Practically: your brown replaces their black, your dark amber replaces their icy accents, and your metals are bronze and gold rather than silver. If a dark color has a golden or reddish core, it is yours.",
      },
      {
        question: "Can a Deep Autumn wear pastels in summer?",
        answer:
          "Pale icy pastels are the fastest way to wash out dark, warm coloring - summer for you means lighter fabrics, not lighter colors. Wear deep camel linen, rich olive cotton, and deep rust or dark amber in breezy cuts instead. If you want lightness near the face, deep camel and warm ivory-adjacent tones are as pale as you should go.",
      },
    ],
  },
  "deep-winter": {
    displayName: "Deep Winter",
    season: "winter",
    subSeason: "deep",
    intro:
      "Deep Winter dresses in shadow and jewel: black, deep navy, wine, and deep emerald worn with cool precision and high contrast. Your coloring is dark and cool, so it demands depth - washed-out pastels and warm earth tones are what betray you, never darkness. Think midnight colors with one gleaming note, like a lit window in a dark street.",
    outfits: [
      {
        name: "Deep Navy Non-Negotiable",
        occasion: "casual",
        description:
          "The everyday Deep Winter baseline: inky layers, clean lines, quiet impact.",
        pieces: [
          "Deep navy heavyweight cotton hoodie",
          "Black straight-leg jeans",
          "Pewter grey leather sneakers",
          "Deep slate beanie",
          "Silver-toned chain necklace",
        ],
        stylingNote:
          "Navy-on-black is a contrast crime on lighter seasons but a signature on you - Deep Winter carries stacked darks without losing definition.",
      },
      {
        name: "Wine Cellar Weekday",
        occasion: "casual",
        description:
          "Dark berry warmth-that-is-not-warm for coffee runs and long afternoons.",
        pieces: [
          "Wine oversized rib-knit sweater",
          "Deep charcoal tapered trousers",
          "Black leather ankle boots",
          "Pewter grey wool scarf",
        ],
        stylingNote:
          "Wine gives your face the flush of a warm tone while staying blue-based - the only kind of red-brown depth your cool coloring accepts.",
      },
      {
        name: "Midnight Green Interval Session",
        occasion: "casual",
        description:
          "Athleisure with the lights turned down - deep tones that still read athletic.",
        pieces: [
          "Midnight green seamless leggings",
          "Black cropped training top",
          "Deep charcoal oversized zip hoodie",
          "Black knit running shoes with pewter grey trim",
          "Deep sapphire water bottle",
        ],
        stylingNote:
          "Deep green and black keep gym gear inside your shadowed harmony - neon activewear visibly cheapens Deep Winter contrast.",
      },
      {
        name: "Deep Charcoal Chairmanship",
        occasion: "work",
        description:
          "The full-authority suit, executed in your palette's most forgiving dark.",
        pieces: [
          "Deep charcoal wool suit blazer",
          "Deep charcoal matching tapered trousers",
          "Deep ruby silk blouse",
          "Black pointed-toe leather pumps",
          "Pewter grey slim watch",
        ],
        stylingNote:
          "Charcoal with a deep ruby core is Deep Winter power dressing - the jewel note keeps high formality from reading flat.",
      },
      {
        name: "Deep Teal Design Review",
        occasion: "work",
        description:
          "A dark jewel tone for days you want presence without a full suit.",
        pieces: [
          "Deep teal fine-knit turtleneck",
          "Black wide-leg tailored trousers",
          "Deep charcoal leather loafers",
          "Pewter grey structured tote",
          "Silver-toned geometric earrings",
        ],
        stylingNote:
          "Deep teal against black gives cool contrast at matched depth - color that reads senior rather than decorative.",
      },
      {
        name: "Dark Oxblood Reservation",
        occasion: "date",
        description:
          "Date-night darkness with a pulse - oxblood as the whole story.",
        pieces: [
          "Dark oxblood satin slip dress",
          "Black cropped leather moto jacket",
          "Black strappy stiletto sandals",
          "Deep magenta lip and silver-toned drop earrings",
        ],
        stylingNote:
          "Oxblood is your seduction shade - blue-red depth that flatters cool skin under low light the way pale pink never could.",
      },
      {
        name: "Aubergine Opera Hours",
        occasion: "evening",
        description:
          "Formal evening drama in dark violet - regal without a single sequin.",
        pieces: [
          "Aubergine velvet column gown",
          "Black satin opera gloves",
          "Pewter grey metallic clutch",
          "Deep sapphire crystal earrings",
          "Black heeled sandals",
        ],
        stylingNote:
          "Aubergine velvet swallows light beautifully on high-contrast coloring - Deep Winter evening wear is about depth, not sparkle.",
      },
      {
        name: "Deep Emerald City Weekend",
        occasion: "weekend",
        description:
          "A jewel-toned weekend look for galleries, markets, and long city walks.",
        pieces: [
          "Deep emerald wool overshirt",
          "Black slim jeans",
          "Deep charcoal chunky-sole Chelsea boots",
          "Pewter grey crossbody bag",
          "Wine knit beanie",
        ],
        stylingNote:
          "Emerald at full depth is your most flattering green - clear, cool, and dark, it sharpens the contrast your coloring runs on.",
      },
      {
        name: "Deep Slate Everyday Armor",
        occasion: "casual",
        description:
          "Tonal blue-grey dressing for days you want to look composed with zero effort.",
        pieces: [
          "Deep slate boxy sweatshirt",
          "Deep navy relaxed jeans",
          "Black minimal leather sneakers",
          "Deep plum knit scarf",
        ],
        stylingNote:
          "Slate over navy keeps casualwear cool and dimensional - the deep plum accent supplies the jewel note every Deep Winter outfit wants.",
      },
      {
        name: "Espresso Exception Cold Snap",
        occasion: "casual",
        description:
          "Deep-freeze layers using your palette's one permitted brown.",
        pieces: [
          "Espresso wool-blend longline puffer coat",
          "Deep ruby chunky turtleneck sweater",
          "Black straight corduroy trousers",
          "Deep charcoal shearling-lined boots",
          "Pewter grey knit gloves",
        ],
        stylingNote:
          "Espresso is the darkest, coolest brown - the only one that works on you - and against deep ruby it reads intentional, not autumnal.",
      },
      {
        name: "Prussian Blue Rainline",
        occasion: "casual",
        description:
          "Wet-weather layers that treat grey skies as a backdrop.",
        pieces: [
          "Prussian blue waterproof rain trench",
          "Deep charcoal merino crewneck sweater",
          "Black slim-fit jeans",
          "Black lug-sole rain boots",
          "Deep magenta umbrella",
        ],
        stylingNote:
          "Prussian blue from your complementary set is storm-dark and cool - rain gear that deepens rather than dulls your contrast.",
      },
      {
        name: "Deep Sapphire Final Round",
        occasion: "work",
        description:
          "Interview-grade formalwear with one deep jewel where the personality lives.",
        pieces: [
          "Deep sapphire crepe sheath dress",
          "Black longline blazer",
          "Deep charcoal pointed pumps",
          "Silver-toned bar necklace",
          "Black leather portfolio",
        ],
        stylingNote:
          "Sapphire under black tailoring signals precision and depth at once - your coloring makes the dark-on-dark layering legible.",
      },
      {
        name: "Deep Magenta Team Offsite",
        occasion: "work",
        description:
          "Smart-casual with an assertive cool accent for workshop-and-dinner days.",
        pieces: [
          "Deep magenta fine-knit sweater",
          "Deep navy tailored ankle trousers",
          "Black leather loafers",
          "Pewter grey laptop tote",
          "Deep slate wool blazer for the evening",
        ],
        stylingNote:
          "Deep magenta is your boldest daytime color - blue-based and saturated at depth, it energizes cool skin without breaking dress codes.",
      },
      {
        name: "Dark Cherry Candlelight Date",
        occasion: "date",
        description:
          "A second-look-guaranteed dinner outfit in near-black red.",
        pieces: [
          "Dark cherry velvet wrap top",
          "Black satin bias-cut midi skirt",
          "Black heeled ankle boots",
          "Silver-toned threader earrings",
          "Deep charcoal wool coat over the shoulders",
        ],
        stylingNote:
          "Dark cherry velvet catches candlelight in flashes - controlled drama that suits Deep Winter's shadowed intensity perfectly.",
      },
      {
        name: "Deep Ruby Black-Tie Wedding",
        occasion: "evening",
        description:
          "A formal wedding-guest gown look built on your most celebratory jewel.",
        pieces: [
          "Deep ruby satin off-shoulder gown",
          "Black velvet clutch",
          "Black strappy heeled sandals",
          "Silver-toned chandelier earrings",
          "Pewter grey satin wrap",
        ],
        stylingNote:
          "Deep ruby is your gala color - cool-blooded red at full depth that photographs like royalty against dark hair and cool skin.",
      },
      {
        name: "Midnight Green Weekend North",
        occasion: "weekend",
        description:
          "Cold-weekend-away layers for mountain towns and fireside dinners.",
        pieces: [
          "Midnight green quilted parka",
          "Deep slate chunky roll-neck sweater",
          "Deep charcoal straight jeans",
          "Black waterproof hiking boots",
          "Wine plaid scarf",
        ],
        stylingNote:
          "Midnight green outerwear over slate and charcoal keeps a weekend kit deep, cool, and coherent - Deep Winter looks best dressed like dusk.",
      },
    ],
    capsule: [
      "Black straight-leg jeans",
      "Deep charcoal wool suit blazer",
      "Deep ruby silk blouse",
      "Deep navy heavyweight hoodie",
      "Deep teal fine-knit turtleneck",
      "Black wide-leg tailored trousers",
      "Dark oxblood satin slip dress",
      "Prussian blue waterproof rain trench",
      "Black leather ankle boots",
      "Wine oversized rib-knit sweater",
    ],
    faqs: [
      {
        question: "Can a Deep Winter wear black?",
        answer:
          "Yes - black is literally one of your palette neutrals, and Deep Winter is one of the few seasons that wears it near the face without penalty. The refinement is pairing: black with a jewel like deep ruby, deep teal, or deep magenta looks richer than black alone. Head-to-toe black works too, as long as texture or one deep accent keeps it from going flat.",
      },
      {
        question: "What colors should a Deep Winter wear to a wedding?",
        answer:
          "Deep ruby, deep emerald, aubergine, and deep sapphire in satin or velvet are your strongest guest colors - formal depth that photographs beautifully on high-contrast coloring. Black accessories are fully allowed for you, and silver-toned or pewter jewelry beats gold. Avoid warm ivory and terracotta, both explicit avoid colors that will clash with your cool depth.",
      },
      {
        question: "Can a Deep Winter wear brown?",
        answer:
          "Only the darkest, coolest brown: espresso, which sits in your neutral set. Warm camel, golden browns, and terracotta are on your avoid list - they introduce a golden cast your cool undertone rejects. Style espresso the way you would black outerwear or boots, and keep the colors around it unmistakably cool.",
      },
      {
        question: "What should a Deep Winter wear in summer without pastels?",
        answer:
          "Keep your colors and lighten the fabric: deep navy linen, black cotton poplin, wine or deep teal in gauzy weaves all read summery through texture rather than paleness. High contrast still works in heat - a black tank with pewter grey linen trousers beats any washed-out pastel on you. If you want light, use pewter grey or a crisp cool-toned white rather than cream.",
      },
    ],
  },
  "true-winter": {
    displayName: "True Winter",
    season: "winter",
    subSeason: "true",
    intro:
      "True Winter is pure contrast: black, bright white, and colors so clear they hum - ruby, ultramarine, fuchsia. Your coloring is cool and high-voltage, so outfits should be graphic and decisive: crisp edges, saturated statements, icy neutrals, and absolutely nothing beige, dusty, or golden. If Bright Spring is neon in sunlight, you are neon against snow.",
    outfits: [
      {
        name: "Black and White Standard Issue",
        occasion: "casual",
        description:
          "The True Winter default: maximum contrast from just two non-colors.",
        pieces: [
          "Bright white boxy heavyweight tee",
          "Black wide-leg jeans",
          "Black-and-bright-white leather sneakers",
          "Silver chain-link necklace",
          "Graphite canvas tote",
        ],
        stylingNote:
          "Black against bright white recreates your natural hair-skin contrast - the graphic baseline every other season has to fake.",
      },
      {
        name: "Electric Blue Corner Booth",
        occasion: "casual",
        description:
          "One voltage-level color over black basics for everyday plans.",
        pieces: [
          "Electric blue oversized sweatshirt",
          "Black slim-fit jeans",
          "Bright white chunky sneakers",
          "Silver small hoop earrings",
        ],
        stylingNote:
          "Electric blue at full saturation feeds off your cool clarity - the same shade that overwhelms muted seasons simply matches your volume.",
      },
      {
        name: "Fuchsia Interval Training",
        occasion: "casual",
        description:
          "High-intensity athleisure that keeps pace with high-intensity coloring.",
        pieces: [
          "Fuchsia seamless leggings",
          "Black racerback sports bra",
          "Bright white lightweight running jacket",
          "Black knit trainers with silver detailing",
          "Cool grey sweat headband",
        ],
        stylingNote:
          "Fuchsia-plus-black is True Winter athletic wear at its best - blue-based brightness framed by your strongest neutral.",
      },
      {
        name: "Graphite Verdict Day",
        occasion: "work",
        description:
          "Sharp monochrome tailoring for days that call for cold precision.",
        pieces: [
          "Graphite sharply tailored blazer",
          "Graphite matching cigarette trousers",
          "Bright white poplin shirt with a crisp collar",
          "Black patent pointed pumps",
          "Silver slim-band watch",
        ],
        stylingNote:
          "Graphite with bright white is high-contrast authority in neutral form - True Winter reads powerful before adding any color at all.",
      },
      {
        name: "Ultramarine All Hands",
        occasion: "work",
        description:
          "Commanding color for presentations and camera days.",
        pieces: [
          "Ultramarine fine-knit crewneck sweater",
          "Black tailored wide-leg trousers",
          "Black leather loafers",
          "Silver geometric stud earrings",
          "Cool grey structured tote",
        ],
        stylingNote:
          "Ultramarine is your working blue - deeper than electric, still fully saturated, it holds authority on screen where dusty blues vanish.",
      },
      {
        name: "Ruby Late Reservation",
        occasion: "date",
        description:
          "The definitive red-dress night, in the red that was made for you.",
        pieces: [
          "Ruby stretch-crepe midi dress with a square neckline",
          "Black cropped tuxedo jacket",
          "Black strappy stiletto heels",
          "Silver drop earrings and a ruby lip",
        ],
        stylingNote:
          "Blue-based ruby is the most flattering red in existence for True Winter - it flashes against cool skin like a signal flare.",
      },
      {
        name: "Bright Purple Gala Circuit",
        occasion: "evening",
        description:
          "Full-saturation formalwear that owns flash photography.",
        pieces: [
          "Bright purple satin column gown",
          "Silver metallic clutch",
          "Bright white satin heeled sandals",
          "Silver crystal chandelier earrings",
          "Black velvet wrap",
        ],
        stylingNote:
          "Saturated purple at floor length is a chroma statement only high-contrast cool coloring can anchor - your version of quiet luxury is loud color.",
      },
      {
        name: "Cyan Harbor Saturday",
        occasion: "weekend",
        description:
          "Crisp waterfront brightness for weekend wandering.",
        pieces: [
          "Cyan cotton pique polo",
          "Bright white straight-leg jeans",
          "Black minimal leather sandals",
          "Silver aviator sunglasses",
          "Cool grey canvas duffel",
        ],
        stylingNote:
          "Cyan against white jeans is winter clarity in summer form - icy, saturated, and impossible to mistake for any warm season.",
      },
      {
        name: "Frost Line Errands",
        occasion: "casual",
        description:
          "An icy-neutral outfit that proves your pale tones are cold, never creamy.",
        pieces: [
          "Frost fine-gauge turtleneck",
          "Cool grey straight-leg trousers",
          "Bright white leather sneakers",
          "Black puffer vest",
          "Silver signet ring",
        ],
        stylingNote:
          "Frost is your pastel - an icy near-white with blue in its veins, nothing like the cream that sits on your avoid list.",
      },
      {
        name: "Black Ice Deep Freeze",
        occasion: "casual",
        description:
          "Serious-cold layers with a hot-pink pulse to cut the monochrome.",
        pieces: [
          "Black longline down puffer coat",
          "Hot pink chunky knit scarf",
          "Cool grey merino turtleneck sweater",
          "Black straight-leg corduroy trousers",
          "Black waterproof snow boots with bright white laces",
        ],
        stylingNote:
          "All-black winterwear is your home turf - one hot pink strike at the neck keeps the contrast graphic instead of gloomy.",
      },
      {
        name: "Limestone Gallery Loop",
        occasion: "casual",
        description:
          "Soft-spoken for a True Winter - pale stone tones sharpened with black.",
        pieces: [
          "Limestone oversized knit sweater",
          "Black slim ankle-length trousers",
          "Black pointed leather flats",
          "Silver cuff bracelet",
        ],
        stylingNote:
          "Limestone is your rare quiet neutral - cool and stony, it still needs black beside it to preserve the contrast your face expects.",
      },
      {
        name: "Bright Magenta Quarterly Pitch",
        occasion: "work",
        description:
          "A statement blazer look for the meetings that decide budgets.",
        pieces: [
          "Bright magenta structured blazer",
          "Black silk camisole",
          "Black tailored straight trousers",
          "Black pointed slingback heels",
          "Silver collar necklace",
        ],
        stylingNote:
          "A saturated magenta jacket over black is True Winter power dressing - color as authority, with your palette's contrast doing the framing.",
      },
      {
        name: "Cool Grey Systems Day",
        occasion: "work",
        description:
          "Quietly rigorous workwear for heads-down days with surprise meetings.",
        pieces: [
          "Cool grey fine-knit polo sweater",
          "Black pleated midi skirt",
          "Black leather Chelsea boots",
          "Silver mesh-strap watch",
          "Electric blue laptop sleeve peeking from a black tote",
        ],
        stylingNote:
          "Cool grey keeps daily workwear icy rather than warm, and the electric blue accent supplies the jolt of clarity your look needs.",
      },
      {
        name: "Hot Pink First Impression",
        occasion: "date",
        description:
          "A bold daytime-into-evening date outfit that does not whisper.",
        pieces: [
          "Hot pink satin wrap top",
          "Black high-waisted flared trousers",
          "Black heeled mules",
          "Silver threader earrings",
          "Bright white mini shoulder bag",
        ],
        stylingNote:
          "Hot pink is blue-based flirtation at full volume - on True Winter contrast it reads magnetic rather than loud.",
      },
      {
        name: "Bright White Winter Wedding",
        occasion: "evening",
        description:
          "A sleek evening wedding-guest look in icy monochrome - assuming the couple approves white-adjacent looks, swap to ruby if not.",
        pieces: [
          "Ultramarine bias-cut satin gown",
          "Bright white tailored evening coat",
          "Silver strappy heeled sandals",
          "Frost crystal drop earrings",
          "Black satin clutch",
        ],
        stylingNote:
          "Ultramarine under a bright white coat is the True Winter formal formula - saturated jewel color inside an icy, high-contrast frame.",
      },
      {
        name: "Graphite Powder Weekend",
        occasion: "weekend",
        description:
          "Ski-town layers that look as sharp in the lodge as on the lift line.",
        pieces: [
          "Graphite technical shell jacket",
          "Cyan merino base layer top",
          "Black insulated ski trousers",
          "Black snow boots",
          "Bright white fleece-lined beanie",
        ],
        stylingNote:
          "Graphite and cyan against snow is True Winter in its natural habitat - icy neutrals, one saturated flash, maximum contrast.",
      },
    ],
    capsule: [
      "Black wide-leg jeans",
      "Bright white poplin shirt",
      "Graphite sharply tailored blazer",
      "Ruby stretch-crepe midi dress",
      "Electric blue oversized sweatshirt",
      "Black tailored straight trousers",
      "Frost fine-gauge turtleneck",
      "Black longline down puffer coat",
      "Black patent pointed pumps",
      "Fuchsia satin wrap top",
    ],
    faqs: [
      {
        question: "Can a True Winter wear black?",
        answer:
          "Not only can you - black is a core True Winter neutral and arguably no one wears it better. Your high natural contrast keeps black from flattening you the way it does warm and muted seasons. The only upgrade is adding one icy or saturated note - bright white collar, ruby lip, silver jewelry - so the look reads designed rather than default.",
      },
      {
        question: "What colors should a True Winter wear to a wedding?",
        answer:
          "Ruby, ultramarine, bright purple, and fuchsia in sleek satins and crepes are your wedding-guest power moves - saturated jewel tones that photograph crisply on high-contrast coloring. Silver jewelry and black accessories finish the look; keep any white pieces away from bridal territory out of courtesy. Avoid cream, warm gold, and rust orange - all three are explicit avoid colors on you.",
      },
      {
        question: "Can a True Winter wear beige or camel to work?",
        answer:
          "Warm camel and cream are on your avoid list - they cast a yellow shadow on cool skin and read tired within an hour. Your light workwear neutrals are bright white, frost, limestone, and cool grey, all of which do beige's softening job without the warmth. A cool grey suit with a bright white shirt outperforms any camel blazer on you.",
      },
      {
        question: "How do True Winter outfits differ from Bright Winter outfits?",
        answer:
          "Both wear saturated cool color, but True Winter is defined by extremes - pure black, bright white, primary-strength ruby and ultramarine - while Bright Winter softens the neutrals slightly and leans on icy pastels as accents. If your best looks are stark black-and-white with one screaming color, you are True; if you glow more in sapphire with icy pink, you are Bright. In practice, True Winter outfits tolerate the hardest contrast of any season.",
      },
    ],
  },
  "bright-winter": {
    displayName: "Bright Winter",
    season: "winter",
    subSeason: "cool",
    intro:
      "Bright Winter pairs cool, jewel-clear color with icy light: sapphire, magenta, and violet set against cool white, slate, and shimmering ice blue. Your coloring is cool with high clarity, so outfits need saturated gemstone hues plus frosted accents - warm beige and golden brown are the fastest way to switch your glow off. Dress like stained glass with snow behind it.",
    outfits: [
      {
        name: "Magenta Signal Casual",
        occasion: "casual",
        description:
          "One jewel-bright hero over icy basics - the everyday Bright Winter formula.",
        pieces: [
          "Magenta boxy cotton sweatshirt",
          "Cool white straight-leg jeans",
          "Slate suede sneakers",
          "Silver-toned pendant necklace",
          "Ice blue beaded bracelet",
        ],
        stylingNote:
          "Magenta at full clarity against cool white matches your natural contrast - dusty or beige-based casuals are what make you look tired.",
      },
      {
        name: "Periwinkle Bookshop Loop",
        occasion: "casual",
        description:
          "A softer-volume day look that stays cool and clear.",
        pieces: [
          "Periwinkle fine-knit crewneck sweater",
          "Charcoal slim jeans",
          "Cool white leather sneakers",
          "Icy lavender knit scarf",
        ],
        stylingNote:
          "Periwinkle is your casual blue-violet - lighter than sapphire but still clear, so it brightens cool skin without breaking harmony.",
      },
      {
        name: "Azure Sprint Session",
        occasion: "casual",
        description:
          "Cold-clear athleisure with real speed in the color.",
        pieces: [
          "Azure high-rise leggings",
          "Cool white cropped training tank",
          "Charcoal lightweight running vest",
          "Cool white-and-slate knit trainers",
          "Icy mint water bottle",
        ],
        stylingNote:
          "Clear azure keeps workout gear at your chroma level - jewel-toned energy where muted sage kits would grey you out mid-run.",
      },
      {
        name: "Sapphire Standing Agenda",
        occasion: "work",
        description:
          "Jewel-toned tailoring that runs the room without raising its voice.",
        pieces: [
          "Sapphire crepe blazer",
          "Cool white silk shell top",
          "Charcoal tailored cigarette trousers",
          "Slate pointed-toe pumps",
          "Silver-toned bar earrings",
        ],
        stylingNote:
          "A sapphire jacket over charcoal is Bright Winter's navy suit - deeper-than-bright color with the icy white keeping contrast crisp.",
      },
      {
        name: "Burgundy Contract Review",
        occasion: "work",
        description:
          "Your darkest power color deployed for serious calendar days.",
        pieces: [
          "Burgundy fine-knit turtleneck",
          "Slate wide-leg tailored trousers",
          "Charcoal leather loafers",
          "Pewter structured tote",
          "Silver-toned link watch",
        ],
        stylingNote:
          "Burgundy gives you near-black depth with a cool wine cast - authority that flatters where warm browns would betray your undertone.",
      },
      {
        name: "Rose Cut Diamond Date",
        occasion: "date",
        description:
          "Cool-toned romance with an icy shimmer finish.",
        pieces: [
          "Rose satin slip dress",
          "Icy pink sheer organza wrap",
          "Silver strappy heeled sandals",
          "Ice blue crystal drop earrings",
        ],
        stylingNote:
          "Cool rose is your romantic register - pink with blue blood, amplified by icy accents instead of warmed down by blush tones.",
      },
      {
        name: "Violet Hour Black Tie",
        occasion: "evening",
        description:
          "Full-length jewel drama for galas, premieres, and milestone parties.",
        pieces: [
          "Violet satin column gown",
          "Charcoal velvet clutch",
          "Silver metallic heeled sandals",
          "Icy lavender crystal ear climbers",
          "Cool white faux-fur stole",
        ],
        stylingNote:
          "Saturated violet at floor length is peak Bright Winter formalwear - gemstone color that needs your cool clarity to stay luminous.",
      },
      {
        name: "Lapis Long Weekend",
        occasion: "weekend",
        description:
          "A crisp city-break uniform for museums, trains, and rooftop bars.",
        pieces: [
          "Lapis merino crewneck sweater",
          "Cool white wide-leg jeans",
          "Slate leather Chelsea boots",
          "Pewter crossbody bag",
          "Icy blue silk scarf knotted at the neck",
        ],
        stylingNote:
          "Lapis is your travel-proof blue - deep, clear, and cool, it stays vivid in photos from grey-sky mornings to neon evenings.",
      },
      {
        name: "Ice Blue Thaw Errands",
        occasion: "casual",
        description:
          "Frosted pastels done the winter way - icy, never sweet.",
        pieces: [
          "Ice blue oversized cable-knit sweater",
          "Cool white slim jeans",
          "Slate suede ankle boots",
          "Silver-toned huggie hoops",
        ],
        stylingNote:
          "Ice blue is a Bright Winter pastel because it is frozen, not powdered - blue-based shimmer where warm beige would deaden your skin.",
      },
      {
        name: "Charcoal Front Line Freeze",
        occasion: "casual",
        description:
          "Deep-winter outerwear with a jewel accent doing the talking.",
        pieces: [
          "Charcoal wool-blend longline coat",
          "Berry chunky turtleneck sweater",
          "Cool slate straight-leg jeans",
          "Charcoal shearling-lined snow boots",
          "Icy pink knit beanie",
        ],
        stylingNote:
          "Charcoal outerwear framed by berry and icy pink keeps January outfits high-contrast and cool - your palette thrives exactly when the weather worsens.",
      },
      {
        name: "Cool Slate Studio Sunday",
        occasion: "casual",
        description:
          "Relaxed tonal dressing for creative errands and cafe laptop hours.",
        pieces: [
          "Cool slate oversized hoodie",
          "Charcoal tapered joggers",
          "Cool white chunky sneakers",
          "Violet enamel keychain on a pewter tote",
        ],
        stylingNote:
          "Slate-on-charcoal athleticwear stays icy and deliberate, with the violet accent supplying the clarity your face reads best against.",
      },
      {
        name: "Berry Analytics Briefing",
        occasion: "work",
        description:
          "Deep pink-red separates for days you present the numbers.",
        pieces: [
          "Berry silk button-down blouse",
          "Charcoal pleated midi skirt",
          "Slate block-heel pumps",
          "Silver-toned collar-tip necklace",
          "Cool white leather portfolio",
        ],
        stylingNote:
          "Berry is your credibility color - saturated, cool, and deep enough for formality while keeping your complexion vivid under office light.",
      },
      {
        name: "Icy Lavender Hybrid Thursday",
        occasion: "work",
        description:
          "Softly frosted smart-casual for split home-and-office days.",
        pieces: [
          "Icy lavender fine-gauge cardigan, buttoned",
          "Cool white tailored ankle trousers",
          "Pewter leather loafers",
          "Slate laptop tote",
          "Silver-toned stud earrings",
        ],
        stylingNote:
          "Icy lavender from your frosted set softens workwear without warming it - the pale tones you wear must always be freezer-cold.",
      },
      {
        name: "Azure Aperitivo Date",
        occasion: "date",
        description:
          "An early-evening drinks look that is bright, cool, and a little electric.",
        pieces: [
          "Azure silk camisole",
          "Charcoal high-waisted wide-leg trousers",
          "Silver heeled mules",
          "Icy mint cocktail ring",
          "Cool white cropped blazer for the terrace",
        ],
        stylingNote:
          "Azure against charcoal is flirtation in your native contrast - one clear jewel note framed by cool structure.",
      },
      {
        name: "Berry Frost Wedding Guest",
        occasion: "evening",
        description:
          "A jewel-and-ice formal look for winter and evening weddings.",
        pieces: [
          "Berry pleated chiffon maxi dress",
          "Cool white satin wrap",
          "Silver strappy block heels",
          "Icy blue crystal clutch",
          "Silver-toned chandelier earrings",
        ],
        stylingNote:
          "Berry chiffon with frosted accessories is Bright Winter's wedding formula - saturated jewel depth kept sparkling by icy trim.",
      },
      {
        name: "Periwinkle Powder Day",
        occasion: "weekend",
        description:
          "Slope-to-lodge weekend layers with your palette against the snow.",
        pieces: [
          "Periwinkle insulated ski jacket",
          "Charcoal softshell snow trousers",
          "Cool white thermal base layer",
          "Slate waterproof snow boots",
          "Magenta fleece-lined headband",
        ],
        stylingNote:
          "Periwinkle and magenta against white snow is Bright Winter in its element - clear cool color needs a frosted backdrop, and winter provides it free.",
      },
    ],
    capsule: [
      "Cool white straight-leg jeans",
      "Sapphire crepe blazer",
      "Magenta boxy cotton sweatshirt",
      "Charcoal tailored cigarette trousers",
      "Berry silk button-down blouse",
      "Cool white silk shell top",
      "Charcoal wool-blend longline coat",
      "Rose satin slip dress",
      "Slate leather Chelsea boots",
      "Ice blue oversized cable-knit sweater",
    ],
    faqs: [
      {
        question: "Can a Bright Winter wear black?",
        answer:
          "Black works on Bright Winter better than on any non-winter season, though charcoal is the neutral your palette actually lists and often photographs a touch softer. Use either as a frame for your jewel tones - black or charcoal with magenta, sapphire, or berry beats black alone. What genuinely fails you is not darkness but warmth: camel and golden brown are the real avoid colors.",
      },
      {
        question: "What colors should a Bright Winter wear to a wedding?",
        answer:
          "Berry, sapphire, violet, and magenta in chiffon, satin, or crepe are your strongest wedding-guest colors - clear jewel tones that stay vivid in every photo. Accessorize in silver and icy tones like ice blue or icy pink rather than gold. Skip terracotta and warm beige entirely; both sit on your avoid list and will dull your cool clarity beside them.",
      },
      {
        question: "Can a Bright Winter wear pastels?",
        answer:
          "Yes, but only the frozen kind: icy pink, icy lavender, icy blue, and icy mint - pastels with a blue, shimmery base. Warm powdery pastels like peach and cream read muddy against your cool, clear coloring. The test is temperature: if the pastel looks like sorbet it is wrong, if it looks like frost it is yours.",
      },
      {
        question: "What neutrals should a Bright Winter build outfits on?",
        answer:
          "Cool white, charcoal, slate, and pewter are your four foundation neutrals - all cool, all crisp, all happy under jewel tones. Cool white replaces cream, charcoal replaces brown, and slate covers the casual middle ground. Warm beige and camel are explicit avoid colors, so audit your basics first: swapping a camel coat for charcoal upgrades every outfit it touches.",
      },
    ],
  },
};
